import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { UserService } from '../services/user.service';
import { DatabaseService } from '../services/database.service';
import { UserSelectionDialogComponent } from '../user-selection-dialog/user-selection-dialog.component';
import { GraphService } from '../services/graph.service';
import { AlertService } from '../services/alert.service';
import { OptionsService } from '../services/options.service';
import { MatDialog } from '@angular/material/dialog';
import { EventVersionHistoryDialogComponent, EventChangeRecord } from '../event-version-history-dialog/event-version-history-dialog.component';

@Component({
  selector: 'app-event-request',
  templateUrl: './event-request.component.html',
  styleUrls: ['./event-request.component.scss'],
  standalone: false
})
export class EventRequestComponent implements AfterViewInit, OnDestroy {
  /** If backend automatically persists history diffs set true; if false we'll POST changes explicitly */
  private serverAutoCapturesHistory = true; // backend diffs on event update; client posts created duplicate, value-less rows
  isLoading = false; // Controls the spinner visibility
  spinnerMessage = 'Loading...'; // Message to display on the spinner
  eventForm!: FormGroup;
  cachedForm: any;
  requestorLink: string = ''; // Link for the requestor email
  eventLeaderLink: string = ''; // Link for the event leader email
  isRequestorLinkVisible = false; // Controls the visibility of the requestor link
  isEventLeaderLinkVisible = false; // Controls the visibility of the event leader link
  // Caption Editor (similar behavior as requestor)
  captionEditorLink: string = '';
  captionEditorName: string = '';
  captionEditorPicture: string = '';
  isCaptionEditorLinkVisible = false;
  // Video Editor (similar behavior as event leader)
  videoEditorLink: string = '';
  videoEditorName: string = '';
  isVideoEditorLinkVisible = false;
  primaryPresentingLocations: any[] = [];
  missingFields: string[] = [];
  dataClassifications = ['GM Public', 'GM Confidential', 'GM Secret']; // Options for Data Classification
  custNumberOptions: any;
  // To add a multi select field, you need to create a dictionary of options, make sure it is populated in the constructor, and patched in the fetchData
  howTakeQuestionOptions: any;
  audienceOptions: any;
  requirementOptions: any;
  regionOptions: any;
  toolsOptions: any;
  toolGroups: Array<{ parent: { key: string; value: string }; children: Array<{ key: string; value: string }> }> = [];
  responsibleForEditingOptions: any;
  videoSharingPlatformOptions: any
  user: any;
  showAgreement = true; // Show the popup on page load
  event_id: string | null = null;
  allAdmins: any;
  today: Date = new Date();
  hourOptions = Array.from({ length: 24 }, (_, h) => ({
    value: h,
    label: `${(h % 12 === 0 ? 12 : h % 12)} ${h < 12 ? 'AM' : 'PM'}`
  }));
  minuteOptions = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
  creatingNewEvent = true;
  pastSubmitted = false; // Checks if status is submitted or later
  isEditing = true;
  canEdit = true;
  gmHolidays: { date: Date; id: string }[] = [];
  showStickyRequestTitle = false;
  private requestTitleObserver?: IntersectionObserver;

  private requestTitleSection?: ElementRef<HTMLElement>;

  @ViewChild('requestTitleSection')
  set requestTitleSectionRef(element: ElementRef<HTMLElement> | undefined) {
    this.requestTitleSection = element;
    this.attachRequestTitleObserver();
  }

  // Keep a snapshot of the last loaded event (DB state) for diffing
  private originalEventSnapshot: any = null;

  // Track fields to ignore for history (like workflow intermediate changes if desired)
  private historyIgnoreFields: Set<string> = new Set(['workflow']);

  // Map of form keys/payload keys to human labels
  fieldLabels: { [key: string]: string } = {
    requestType: 'Request Type',
    requestTitle: 'Request Title',
    expectedNumberOfParticipants: 'Expected # Participants',
    eventStartDate: 'Event Start Date',
    eventStartTime: 'Event Start Time',
    eventEndDate: 'Event End Date',
    eventEndTime: 'Event End Time',
    primaryPresentingLocation: 'Primary Presenting Location',
    remotePresentingLocation: 'Remote Presenting Location',
    requestor: 'Requestor',
    eventLeader: 'Event Leader',
    eventRequirements: 'Event Requirements',
    setItems: 'Set Items',
    eventDescription: 'Event Description',
    presenters: 'Presenters',
    audience: 'Audience',
    audienceOther: 'Audience Other',
    region: 'Region',
    takeQuestions: 'How Will You Take Questions',
    takeQuestionsOther: 'Questions Other',
    isVideoOnDemandNeeded: 'Video On Demand Needed',
    vodExpirationDate: 'VOD Expiration Date',
    dataClassification: 'Data Classification',
    retentionTime: 'Retention Time',
    assignedTo: 'Assigned To',
    tech: 'Tech',
    highProfileEvent: 'High Profile Event',
    captioning: 'Captioning',
    workRelatedEvent: 'Work Related Event',
    testing: 'Testing',
    toolsToBeUsed: 'Tools To Be Used',
    toolsOther: 'Tools Other',
    programCode: 'Program Code',
    custNumber: 'Customer Number',
    studioNeeds: 'Studio Needs',
    broadcastComments: 'Broadcast Comments',
    gmVideoUrl: 'GM Video URL',
    virtualEventsUrl: 'Virtual Events URL',
    brightcoveUrl: 'Brightcove URL',
    teamsUrl: 'Teams URL',
    editStatus: 'Edit Status',
    videoEditingComments: 'Video Editing Comments',
    eventLength: 'Event Length',
    sendToArchive: 'Send To Archive',
    trimDate: 'Trim Date',
    trimOffline: 'Trim Offline',
    captionOrderDate: 'Caption Order Date',
    captionEditor: 'Caption Editor',
    captionCorrectionTime: 'Caption Correction Time',
    correctedSrtDownloaded: 'Corrected SRT Downloaded',
    captioningComments: 'Captioning Comments',
  videoEditingAssignedTo: 'Video Editor',
    eventOwnershipTransferred: 'Event Ownership Transferred',
    gmVideoAttendees: 'GM Video Attendees',
    venueAttendees: 'Venue Attendees',
    brightcoveAttendees: 'Brightcove Attendees',
    virtualEventsAttendees: 'Virtual Events Attendees',
    teamsAttendees: 'Teams Attendees',
    socialMediaAttendees: 'Social Media Attendees',
    audioConferenceAttendees: 'Audio Conference Attendees',
    gmvcRoomCount: 'GMVC Room Count',
    studioServicesUsed: 'Studio Services Used'
  };

  constructor(private router: Router, private route: ActivatedRoute,
    private dialog: MatDialog, private fb: FormBuilder, private _user: UserService,
    private _db: DatabaseService, private _graph: GraphService, private _alert: AlertService, private _options: OptionsService) {

    let formControls = {
      workflow: ['New'],
      requestType: ['Live Event', Validators.required],
      requestTitle: ['', Validators.required],
      requestStatus: ['Pending', Validators.required],
      expectedNumberOfParticipants: [''],
      eventStartDate: ['', Validators.required],
      eventEndDate: ['', Validators.required],
      isAllDay: [false],
      startHour: ['', Validators.required],          // NOW stores 0–23
      startMinute: ['', Validators.required],
      endHour: ['', Validators.required],            // NOW stores 0–23
      endMinute: ['', Validators.required],
      event_id: [''],
      primaryPresentingLocation: [''],
      SpecifyYouOwnValuePresentingLocation: [false],
      otherLocation: [''],
      remotePresentingLocation: [''],
      requestor: ['', Validators.required],
      requestorMeta: [null],
      eventLeader: [''],
      eventLeaderMeta: [null],
      eventRequirements: [''],
      setItems: [''],
      eventDescription: [''],
      presenters: ['',],
      audience: [''],
      audSpecifyYourOwnValue: [false],
      audienceOther: [''],
      region: [''],
      takeQuestions: [''],
      SpecifyYourOwnValueQuestions: [false],
      takeQuestionsOther: [''],
      isVideoOnDemandNeeded: ['Yes'],
      vodExpirationDate: [this.getDefaultVodExpirationDate()],
      dataClassification: ['GM Confidential'],
      retentionTime: ['1 Year'],
      // Recording Request
      recordingLocation: [''],
      SpecifyYourOwnValueRecordingLocation: [false],
      otherRecordingLocation: [''],
      additionalDetailsForLocation: [''],
      responsibleForEditing: [''],
      SpecifyYourOwnValueResponsibleForEditing: [false],
      responsibleForEditingOther: [''],
      videoSharingPlatforms: [''],
      SpecifyYourOwnValueVideoSharingPlatforms: [false],
      videoSharingPlatformsOther: [''],
      videoFileRecipient: [''],
      // Admin - Broadcast Detail
      assignedTo: [''],
      tech: [''],
      highProfileEvent: ['No'],
      captioning: ['Yes'],
      workRelatedEvent: ['Yes'],
      testing: ['No'],
      SpecifyYourOwnValueTools: [false],
      toolsOther: [''],
      toolsToBeUsed: [''],
      programCode: [''],
      custNumber: [''],
      studioNeeds: [''],
      broadcastComments: [''],
      gmVideoUrl: [''],
      virtualEventsUrl: [''],
      brightcoveUrl: [''],
      teamsUrl: [''],
      // Admin - Video Edit
      sendToArchive: ['No'],
      captionEditor: [''],
      videoEditingAssignedTo: [''],
      eventOwnershipTransferred: ['No'],
      // Admin - Metric Edit
      gmVideoAttendees: [0],
      venueAttendees: [0],
      brightcoveAttendees: [0],
      virtualEventsAttendees: [0],
      teamsAttendees: [0],
      socialMediaAttendees: [0],
      audioConferenceAttendees: [0],
      gmvcRoomCount: [0],
      studioServicesUsed: ['No']
    };

    this.eventForm = this.fb.group(formControls);
  }

  ngAfterViewInit(): void {
    this.attachRequestTitleObserver();
  }

  ngOnDestroy(): void {
    this.requestTitleObserver?.disconnect();
  }

  private attachRequestTitleObserver(): void {
    if (!this.requestTitleSection?.nativeElement) {
      this.showStickyRequestTitle = false;
      return;
    }

    this.requestTitleObserver?.disconnect();
    const element = this.requestTitleSection.nativeElement;

    this.requestTitleObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          this.showStickyRequestTitle = false;
          return;
        }

        const scrolledPast = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
        this.showStickyRequestTitle = scrolledPast;
      },
      { root: null, threshold: 1 }
    );

    this.requestTitleObserver.observe(element);
  }

  // Safe accessor for the current event id (used in template)
  get eventId(): string | null {
    return this.eventForm?.get('event_id')?.value || null;
  }

  ngOnInit() {
    // If migrating from old form that still has AM/PM controls, remove them safely
    if (this.eventForm.get('startAmPm')) this.eventForm.removeControl('startAmPm');
    if (this.eventForm.get('endAmPm')) this.eventForm.removeControl('endAmPm');

    this.eventForm.get('requestType')?.valueChanges.subscribe(() => {
      setTimeout(() => {
        this.attachRequestTitleObserver();
      }, 0);
    });
    
    this.isLoading = true;
    this._user.user$.subscribe(user => {
      if (user && user !== null) {
        this.user = user;
        const requestorMeta = {
          gmid: user.gmid || this.getGmidFromUserPrincipalName(user.userPrincipalName),
          email: user.mail || user.userPrincipalName || '',
          name: user.displayName || user.name || ''
        };
        this.eventForm.patchValue({
          requestor: requestorMeta.email,
          requestorMeta: requestorMeta,
        });
        // Set the requestor link to the user's People Finder link
        this.requestorName = requestorMeta.name;
        this.requestorLink = `https://peoplefinder.gm.com/person/show/${requestorMeta.gmid}`;
        this.isRequestorLinkVisible = true; // Show the requestor link
        if (!this.eventForm.get('captionEditor')?.value) {
          this.eventForm.patchValue({
            captionEditor: user.displayName,
          });
          const captionEditorGmid = user.gmid || this.getGmidFromUserPrincipalName(user.userPrincipalName);
          this.captionEditorLink = captionEditorGmid ? `https://peoplefinder.gm.com/person/show/${captionEditorGmid}` : '';
          this.isCaptionEditorLinkVisible = true;
        }
      }
    });
    this._db.getAllAdmins().subscribe((admins) => {
      this.allAdmins = admins.admins;
    });
    this.route.queryParamMap.subscribe((params) => {
      this.event_id = params.get('id');
      if (this.event_id) {
        this.isLoading = true;
        this.pastSubmitted = false;
        this.canEdit = false;
        this.creatingNewEvent = false;
      } else {
        this.isLoading = false;
      }
    });
    this._options.getAllOptions().subscribe({
      next: (options) => {
        this.primaryPresentingLocations = this.translateOptionsForUse(options, "Primary Presenting Locations");
        this.custNumberOptions = this.translateOptionsForUse(options, "Customer Numbers");

        this.howTakeQuestionOptions = this.translateOptionsForUse(options, "How To Take Questions");
        this.requirementOptions = this.translateOptionsForUse(options, "Requirements");
        this.audienceOptions = this.translateOptionsForUse(options, "Audience");
        this.regionOptions = this.translateOptionsForUse(options, "Regions");
        this.toolsOptions = this.translateOptionsForUse(options, "Tools");
        this.toolGroups = this.buildToolGroups(this.toolsOptions);
        this.responsibleForEditingOptions = this.translateOptionsForUse(options, "Responsible For Editing");
        this.videoSharingPlatformOptions = this.translateOptionsForUse(options, "Video Sharing Platforms");

        if (this.howTakeQuestionOptions) {
          this.addOptionControlsToExistingForm(this.howTakeQuestionOptions);
        }
        if (this.requirementOptions) {
          this.addOptionControlsToExistingForm(this.requirementOptions);
        }
        if (this.audienceOptions) {
          this.addOptionControlsToExistingForm(this.audienceOptions);
        }
        if (this.regionOptions) {
          this.addOptionControlsToExistingForm(this.regionOptions);
        }
        if (this.toolsOptions) {
          this.addOptionControlsToExistingForm(this.toolsOptions);
        }
        if (this.responsibleForEditingOptions) {
          this.addOptionControlsToExistingForm(this.responsibleForEditingOptions);
        }
        if (this.videoSharingPlatformOptions) {
          this.addOptionControlsToExistingForm(this.videoSharingPlatformOptions);
        }

        if (this.event_id) {
          this.fetchEventData(this.event_id);
        }
      },
      error: (error) => {
        console.error('Error loading options:', error);
      }
    });
    this._db.getHolidayEvents().subscribe({
      next: (res) => {
        if (Array.isArray(res)) {
          this.gmHolidays = res
            .filter(h => h.date)
            .map(h => ({
              date: new Date(h.date.replace("T00", "T12")), // Adjust time to avoid timezone issues
              id: h.id || ''
            }));
        }
      },
      error: (error) => {
        console.error('Error loading GM holidays:', error);
      }
    });
  }

  translateOptionsForUse(options: any, name: string) {
    const optionEntry = options.find((opt: any) => opt.name === name);
    if (optionEntry) {
      if (optionEntry.isObject) {
        return JSON.parse(optionEntry.value);
      } else {
        return JSON.parse(optionEntry.value).arr.split(',');
      }
    } else {
      console.error(`Option with name "${name}" not found.`);
    }
  }

  /**
   * Dynamically adds checkbox controls to the existing form group. Needed for multiselect fields
   * @param optionsDict Dictionary of options to add as form controls
   */
  private addOptionControlsToExistingForm(optionsDict: { [key: string]: string }): void {
    if (!this.eventForm) {
      console.error('EventForm not initialized yet');
      return;
    }

    Object.keys(optionsDict).forEach(key => {
      // Only add the control if it doesn't already exist
      if (!this.eventForm.get(key)) {
        this.eventForm.addControl(key, this.fb.control(false));
      }
    });
  }

  /**
   * Helper method to add checkbox controls from option dictionaries to the form controls object
   */
  private addOptionControlsToForm(formControls: any, optionsDict: { [key: string]: string }): void {
    Object.keys(optionsDict).forEach(key => {
      formControls[key] = [false];
    });
  }

  fetchEventData(event_id: string): void {
    this.creatingNewEvent = false;
    this._db.getEventById(event_id).subscribe(
      (data) => {
        console.log('Fetched Event Data:', data);
        const techArray = data.tech ? data.tech.split(', ').filter((t: string) => t.trim()) : [];
        const requestorValue = typeof data.requestor === 'string'
          ? data.requestor
          : (data.requestor?.email || data.requestor?.name || '');
        const requestorMetaValue = (data.requestor && typeof data.requestor === 'object') ? data.requestor : null;
        const eventLeaderValue = typeof data.event_leader === 'string'
          ? data.event_leader
          : (data.event_leader?.email || data.event_leader?.name || '');
        const eventLeaderMetaValue = (data.event_leader && typeof data.event_leader === 'object') ? data.event_leader : null;
        // this.event_id = event_id;
        // Populate the form with the fetched data
        this.eventForm.patchValue({
          event_id: data.event_id,
          workflow: data.workflow,
          requestStatus: data.request_status,
          // User - Request Form
          requestType: data.request_type,
          // Live Event
          requestTitle: data.request_title,
          expectedNumberOfParticipants: data.expected_number_of_participants,
          primaryPresentingLocation: data.primary_presenting_location,
          SpecifyYouOwnValuePresentingLocation: data.other_location ? data.other_location.length : 0,
          otherLocation: data.other_location,
          remotePresentingLocation: data.remote_presenting_location,
          requestor: requestorValue,
          requestorMeta: requestorMetaValue,
          eventLeader: eventLeaderValue,
          eventLeaderMeta: eventLeaderMetaValue,
          eventRequirements: data.event_requirements,
          setItems: data.set_items,
          eventDescription: data.event_description,
          presenters: data.presenters,
          audience: data.audience,
          audienceOther: data.audience_other,
          // Guard against null/undefined before reading length
          audSpecifyYourOwnValue: data.audience_other ? data.audience_other.length : 0,
          region: data.region,
          takeQuestions: data.how_will_you_take_questions,
          takeQuestionsOther: data.how_will_you_take_questions_other,
          SpecifyYourOwnValueQuestions: data.how_will_you_take_questions_other ? data.how_will_you_take_questions_other.length : 0, // guarded length
          isVideoOnDemandNeeded: data.is_video_on_demand_needed ? 'Yes' : 'No',

          vodExpirationDate: data.vod_expiration_date,
          dataClassification: data.data_classification,
          retentionTime: data.retention_time,
          // Recording
          recordingLocation: data.recording_location,
          SpecifyYourOwnValueRecordingLocation: data.other_recording_location?.length,
          otherRecordingLocation: data.other_recording_location,
          additionalDetailsForLocation: data.additional_detail_for_location,
          responsibleForEditing: data.responsible_for_editing,
          SpecifyYourOwnValueResponsibleForEditing: data.responsible_for_editing_other ? data.responsible_for_editing_other.length : 0,
          responsibleForEditingOther: data.responsible_for_editing_other,
          videoSharingPlatforms: data.video_sharing_platforms,
          SpecifyYourOwnValueVideoSharingPlatforms: data.video_sharing_platforms_other ? data.video_sharing_platforms_other.length : 0,
          videoSharingPlatformsOther: data.video_sharing_platforms_other,
          videoFileRecipient: data.video_file_recipient,
          // Special Request

          // Holiday

          // Admin - Broadcast Detail
          assignedTo: data.assigned_to,
          tech: techArray,
          highProfileEvent: data.high_profile_event ? 'Yes' : 'No',
          captioning: data.captioning ? 'Yes' : 'No',
          workRelatedEvent: data.work_related_event ? 'Yes' : 'No',
          testing: data.testing ? 'Yes' : 'No',
          SpecifyYourOwnValueTools: data.tools_other ? data.tools_other.length : 0,
          toolsOther: data.tools_other,
          toolsToBeUsed: data.tools_to_be_used,
          programCode: data.program_code,
          custNumber: data.cust_number,
          studioNeeds: data.studio_needs,
          broadcastComments: data.broadcast_comments,
          gmVideoUrl: data.gm_video_url,
          virtualEventsUrl: data.virtual_events_url,
          brightcoveUrl: data.brightcove_url,
          teamsUrl: data.teams_url,
          // Admin - Video Edit
          sendToArchive: data.send_to_archive ? 'Yes' : 'No',
          captionEditor: data.caption_editor,
          videoEditingAssignedTo: data.video_editing_assigned_to,
          eventOwnershipTransferred: data.event_ownership_transferred ? 'Yes' : 'No',
          // Admin - Metric Edit
          gmVideoAttendees: data.gm_video_attendees || 0,
          venueAttendees: data.venue_attendees || 0,
          brightcoveAttendees: data.brightcove_attendees || 0,
          virtualEventsAttendees: data.virtual_events_attendees || 0,
          teamsAttendees: data.teams_attendees || 0,
          socialMediaAttendees: data.social_media_attendees || 0,
          audioConferenceAttendees: data.audio_conference_attendees || 0,
          gmvcRoomCount: data.gmvc_room_count || 0,
          studioServicesUsed: data.studio_services_used ? 'Yes' : 'No',
          // Calendar ID
          calendar_id: data.calendar_id,
        });

        if (data.start_date) {
          console.log("Start date from DB:", data.start_date);
          const dt = new Date(data.start_date);
          this.eventForm.patchValue({
            eventStartDate: dt,
            startHour: dt.getHours(),                               // 0–23
            startMinute: dt.getMinutes().toString().padStart(2,'0')
          });
        }

        if (data.end_date) {
          const dt = new Date(data.end_date);
            this.eventForm.patchValue({
              eventEndDate: dt,
              endHour: dt.getHours(),
              endMinute: dt.getMinutes().toString().padStart(2,'0')
            });
        }

        if (data.vod_expiration_date) {
          this.eventForm.patchValue({
            vodExpirationDate: new Date(data.vod_expiration_date)
          });
        }

        this.patchOptionControls(data.event_requirements, this.requirementOptions);
        this.patchOptionControls(data.audience, this.audienceOptions);
        this.patchOptionControls(data.region, this.regionOptions);
        this.patchOptionControls(data.how_will_you_take_questions, this.howTakeQuestionOptions);
        this.patchOptionControls(data.tools_to_be_used, this.toolsOptions);
        this.patchOptionControls(data.responsible_for_editing, this.responsibleForEditingOptions);
        this.patchOptionControls(data.video_sharing_platforms, this.videoSharingPlatformOptions);
        
        // Refresh date/time fields after all data is loaded to ensure proper formatting
        this.refreshDateTimeFields();
        
        // Check and set links for specific fields
        const requestorEmailForBackfill = requestorMetaValue?.email || (typeof data.requestor === 'string' ? data.requestor : '');
        if (requestorMetaValue || requestorEmailForBackfill) {
          this.requestorName = requestorMetaValue?.name || requestorEmailForBackfill || '';
          const requestorGmid = requestorMetaValue?.gmid || '';
          this.requestorLink = requestorGmid ? `https://peoplefinder.gm.com/person/show/${requestorGmid}` : '';
          this.isRequestorLinkVisible = !!this.requestorLink;

          // Only backfill if we have email but missing critical data
          // This prevents triggering search dialogs on page load for users with duplicate entries
          if ((!requestorMetaValue?.gmid || !requestorMetaValue?.name) && requestorEmailForBackfill) {
            // Use silent backfill method that won't trigger user selection dialog
            this.backfillRequestorFromEmail(requestorEmailForBackfill);
          }
        } else {
          this.isRequestorLinkVisible = false;
        }
        const eventLeaderEmailForBackfill = eventLeaderMetaValue?.email || (typeof data.event_leader === 'string' ? data.event_leader : '');
        if (eventLeaderMetaValue || eventLeaderEmailForBackfill) {
          this.eventLeaderName = eventLeaderMetaValue?.name || eventLeaderEmailForBackfill || '';
          const eventLeaderGmid = eventLeaderMetaValue?.gmid || '';
          this.eventLeaderLink = eventLeaderGmid ? `https://peoplefinder.gm.com/person/show/${eventLeaderGmid}` : '';
          this.isEventLeaderLinkVisible = !!this.eventLeaderLink;

          // Only backfill if we have email but missing critical data
          // This prevents triggering search dialogs on page load for users with duplicate entries
          if ((!eventLeaderMetaValue?.gmid || !eventLeaderMetaValue?.name) && eventLeaderEmailForBackfill) {
            // Use silent backfill method that won't trigger user selection dialog
            this.backfillEventLeaderFromEmail(eventLeaderEmailForBackfill);
          }
        } else {
          this.isEventLeaderLinkVisible = false;
        }
        if (data.caption_editor) {
          this.initializeCaptionEditorFromStoredValue(data.caption_editor);
        }
        if (data.video_editing_assigned_to) {
          this.initializeVideoEditorFromStoredValue(data.video_editing_assigned_to);
        }
        if (data.workflow.includes('Submitted')) {
          this.pastSubmitted = true;
          this.disableAllFields();
        }

        // Should be checking to see if ID is valid, and if this person should have access to view this request (either admin, requestor, or event leader)
        if (this.user.isAdmin || (this.user.mail && (this.user.mail === this.eventForm.get('requestor')?.value || this.user.mail === this.eventForm.get('eventLeader')?.value))) {
          this.canEdit = true;
        }

        // Cache a version of the form for recognizing changes
        this.cachedForm = this.eventForm.value;
        this.isLoading = false; // Hide the spinner after data is loaded
        // Keep snapshot for version diffing (store only relevant form keys)
        this.originalEventSnapshot = { ...this.eventForm.value };
        console.log('Event data loaded successfully:', this.eventForm.value);
        // Auto-open mock history if flag set and not opened yet
      },
      async (error) => {
        console.error('Error fetching event data:', error);
        this.isLoading = false; // Hide the spinner
        await this._alert.error('Error fetching event data. Please try again later.', this._alert.getErrorDetails(error));
      }
    );
  }

  /**
   * Create a copy of this event (leaving out a few fields), sending it to DB, and navigating to the new event
   * This is useful for creating a similar event without having to fill out all the fields again.
   */
  async clone() {
    const requestType = this.eventForm.get('requestType')?.value;
    const requireEventLeader = ['Live Event', 'Recording', 'Special Request'].includes(requestType);
    const canProceed = await this.ensurePersonMetaResolved({
      requireEventLeader,
      onlyIfEntered: false,
      actionLabel: 'cloning this event'
    });
    if (!canProceed) return;

    const clonedEvent = { ...this.eventForm.value };
    clonedEvent.requestTitle = clonedEvent.requestTitle + ' - Cloned from #' + clonedEvent.event_id;
    clonedEvent.requestStatus = 'Unsubmitted';
    clonedEvent.workflow = 'New';
    clonedEvent.eventStartDate = null;
    clonedEvent.eventEndDate = null;
    clonedEvent.vodExpirationDate = null;
    // Remove fields that should not be cloned, like admin fields
    delete clonedEvent.event_id;
    delete clonedEvent.assignedTo;
    delete clonedEvent.tech;
    clonedEvent.highProfileEvent = 'No';
    clonedEvent.captioning = 'Yes';
    clonedEvent.workRelatedEvent = 'Yes';
    clonedEvent.testing = 'No';
    clonedEvent.SpecifyYourOwnValueTools = false;
    delete clonedEvent.toolsOther;
    clonedEvent.toolsToBeUsed = '';
    delete clonedEvent.programCode;
    delete clonedEvent.custNumber;
    delete clonedEvent.studioNeeds;
    delete clonedEvent.broadcastComments;
    delete clonedEvent.gmVideoUrl;
    delete clonedEvent.virtualEventsUrl;
    delete clonedEvent.brightcoveUrl;
    delete clonedEvent.teamsUrl;
    clonedEvent.sendToArchive = 'No';
    delete clonedEvent.captionEditor;
    delete clonedEvent.videoEditingAssignedTo;
    clonedEvent.eventOwnershipTransferred = 'No';
    clonedEvent.gmVideoAttendees = 0;
    clonedEvent.venueAttendees = 0;
    clonedEvent.brightcoveAttendees = 0;
    clonedEvent.virtualEventsAttendees = 0;
    clonedEvent.teamsAttendees = 0;
    clonedEvent.socialMediaAttendees = 0;
    clonedEvent.audioConferenceAttendees = 0;
    clonedEvent.gmvcRoomCount = 0;
    clonedEvent.studioServicesUsed = 'No';
    delete clonedEvent.calendar_id;

    let payload = this._db.generateEventPayload(clonedEvent, 'New');
    console.log('Payload for cloning:', payload);
    this._db.createEvent(payload).subscribe({
      next: async (response) => {
        console.log('Event cloned successfully:', response);
        // Navigate to the new event's edit page
        console.log("Go to route", '/request?id=' + response.request.event_id);

        this.router.navigate(['/request'], { queryParams: { id: response.request.event_id } });
        this.creatingNewEvent = true;
        this.pastSubmitted = false;
        this.enableAllFields();
        await this._alert.warning('Event cloned successfully. You are now editing the new event! Please set a new start and end date/time as well as changing the Title. This event is currently unsubmitted, make sure to submit it when ready.');

        // reload the page to reset state
        window.location.reload();
      },
      error: async (error) => {
        console.error('Error cloning event:', error);
        await this._alert.error('Error cloning event. Please try again later.', this._alert.getErrorDetails(error));
      }
    });
  }

  /**
   * Dynamically patches form controls for option dictionaries based on comma-separated string
   * @param dataString Comma-separated string from database
   * @param optionsObject The options dictionary to check against
   */
  private patchOptionControls(dataString: string, optionsObject: { [key: string]: string }): void {
    if (!dataString) return;

    const selectedValues = dataString.split(',').map(value => value.trim());

    // Set all checkboxes for this group to false first
    Object.keys(optionsObject).forEach(key => {
      this.eventForm.get(key)?.setValue(false);
    });

    // Set matching checkboxes to true
    Object.keys(optionsObject).forEach(key => {
      if (selectedValues.includes(key)) {
        this.eventForm.get(key)?.setValue(true);
      }
    });
  }
  // Helper to check if two dates are the same day
  private isSameDay(date1: Date, date2: Date): boolean {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    // Only compare month and day to avoid timezone issues
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }
  async onDateChange() {
    console.log('Date change detected');
    const startDate = this.eventForm.get('eventStartDate')?.value;
    const endDate = this.eventForm.get('eventEndDate')?.value;
    const requestType = this.eventForm.get('requestType')?.value;
    const isAllDay = this.eventForm.get('isAllDay')?.value;

    // Check 7-day warning first (before clearing dates due to holidays)
    if (startDate) {
      const differenceInDays = Math.ceil((startDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      if (differenceInDays <= 7) {
        await this._alert.warning('If event is 7 days or less please contact Broadcasting directly');
      }
    }

    let hasHolidayIssue = false;

    // For most request types, block scheduling directly on GM holidays.
    // Exception: allow "Holiday/Vacation/Internal Testing" so admins can
    // record their vacation/holiday time on the calendar.
    const shouldBlockOnHoliday = requestType !== 'Holiday/Vacation/Internal Testing';

    if (shouldBlockOnHoliday) {
      // Check if startDate or endDate matches any GM holiday
      if (startDate && this.gmHolidays.some(h => this.isSameDay(h.date, startDate))) {
        await this._alert.warning('The selected start date is a GM holiday. Please choose a different date.');
        this.eventForm.get('eventStartDate')?.setValue(null);
        this.eventForm.get('eventEndDate')?.setValue(null);
        hasHolidayIssue = true;
      }
      if (endDate && this.gmHolidays.some(h => this.isSameDay(h.date, endDate))) {
        await this._alert.warning('The selected end date is a GM holiday. Please choose a different date.');
        this.eventForm.get('eventStartDate')?.setValue(null);
        this.eventForm.get('eventEndDate')?.setValue(null);
        hasHolidayIssue = true;
      }
    }
    
    // Continue with other logic if no holiday issue
    if (startDate && !hasHolidayIssue) {
      // For all-day holiday/vacation entries, treat the selection as a date RANGE
      // and normalize to full days (start 00:00, end 23:00) while allowing
      // multiple consecutive days.
      if (isAllDay && requestType === 'Holiday/Vacation/Internal Testing') {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);

        let end: Date;
        if (endDate) {
          end = new Date(endDate);
        } else {
          end = new Date(start);
        }

        // Normalize end-of-day time for the final day in the range
        end.setHours(23, 0, 0, 0);

        if (end < start) {
          // If user picked an end date before start, snap back to a single day
          end = new Date(start);
          end.setHours(23, 0, 0, 0);
          await this._alert.warning('End date cannot be before start date. Setting end date to start date.');
        }

        this.eventForm.patchValue({
          eventStartDate: start,
          eventEndDate: end,
          startHour: 0,
          startMinute: '00',
          endHour: 23,
          endMinute: '00'
        });

        // Still base VOD expiration on the first day of the range
        this.updateVodExpirationFromRetention(start);
      } else {
        // Default behavior for non all-day events
        this.updateVodExpirationFromRetention(startDate);

        // Ensure we always have an end date (at least same day as start)
        let effectiveEndDate = endDate;
        if (!effectiveEndDate) {
          effectiveEndDate = startDate;
          this.eventForm.get('eventEndDate')?.setValue(startDate);
        }

        // If end date is before start date, snap it back to start date
        if ((effectiveEndDate && startDate && effectiveEndDate < startDate) && !hasHolidayIssue) {
          effectiveEndDate = startDate;
          this.eventForm.get('eventEndDate')?.setValue(startDate);
          await this._alert.warning("End date cannot be before start date. Setting end date to start date.");
        }

        // Reapply the previously selected start/end times onto the new dates.
        // This prevents times from resetting to 12:00 AM when the date is edited.
        const startHour = this.eventForm.get('startHour')?.value;
        const startMinute = this.eventForm.get('startMinute')?.value;
        const endHour = this.eventForm.get('endHour')?.value;
        const endMinute = this.eventForm.get('endMinute')?.value;

        if (startDate && startHour !== '' && startHour !== null && startMinute !== '' && startMinute !== null) {
          const d = new Date(startDate);
          d.setHours(parseInt(startHour, 10), parseInt(startMinute, 10), 0, 0);
          this.eventForm.get('eventStartDate')?.setValue(d);
        }

        const finalEndDate = this.eventForm.get('eventEndDate')?.value;
        if (finalEndDate && endHour !== '' && endHour !== null && endMinute !== '' && endMinute !== null) {
          const dEnd = new Date(finalEndDate);
          dEnd.setHours(parseInt(endHour, 10), parseInt(endMinute, 10), 0, 0);
          this.eventForm.get('eventEndDate')?.setValue(dEnd);
        }
      }
    }
  }

  /** Toggle handler for the "All day" option used on Holiday/Vacation/Internal Testing. */
  onAllDayToggle() {
    const isAllDay = this.eventForm.get('isAllDay')?.value;
    const requestType = this.eventForm.get('requestType')?.value;
    const startDate = this.eventForm.get('eventStartDate')?.value;

    if (!isAllDay || requestType !== 'Holiday/Vacation/Internal Testing' || !startDate) {
      return;
    }

    // Reuse the same normalization logic as a date change
    // so ranges are supported when the checkbox is toggled.
    this.onDateChange();
  }

  // UPDATED time change logic (no AM/PM fields)
  async onTimeChange() {
    const startHour = this.eventForm.get('startHour')?.value;   // 0–23
    const startMinute = this.eventForm.get('startMinute')?.value;

    let endHour = this.eventForm.get('endHour')?.value;
    let endMinute = this.eventForm.get('endMinute')?.value;

     // Auto-fill end (add 1 hour) if end not set yet
    if (startHour !== '' && startMinute !== '' && (endHour === '' || endMinute === '')) {
      endHour = (parseInt(startHour, 10) + 1) % 24;
      endMinute = startMinute;
      this.eventForm.patchValue({
        endHour: endHour,
        endMinute: endMinute
      });
    }

    if (startHour !== '' && startMinute !== '' &&
        endHour !== '' && endMinute !== '' &&
        this.eventForm.get('eventStartDate')?.value &&
        this.eventForm.get('eventEndDate')?.value) {

      const startDateTime = new Date(this.eventForm.get('eventStartDate')?.value);
      startDateTime.setHours(parseInt(startHour, 10), parseInt(startMinute, 10), 0, 0);

      const endDateTime = new Date(this.eventForm.get('eventEndDate')?.value);
      endDateTime.setHours(parseInt(endHour, 10), parseInt(endMinute, 10), 0, 0);

      if (endDateTime < startDateTime) {
        await this._alert.warning('End time cannot be before start time. Setting end time to start time.');
        this.eventForm.patchValue({
          endHour: parseInt(startHour, 10),
          endMinute: startMinute
        });
        endHour = startHour;
        endMinute = startMinute;
      }
    }

    // Apply selected hours/minutes onto date objects
    if (startHour !== '' && startMinute !== '') {
      const startDate = this.eventForm.get('eventStartDate')?.value;
      if (startDate) {
        const d = new Date(startDate);
        d.setHours(parseInt(startHour, 10), parseInt(startMinute, 10), 0, 0);
        this.eventForm.get('eventStartDate')?.setValue(d);
      }
    }
    
    if (endHour && endHour !== '' && endMinute && endMinute !== '') {
      const endDate = this.eventForm.get('eventEndDate')?.value;
      if (endDate) {
        const d = new Date(endDate);
        d.setHours(parseInt(endHour, 10), parseInt(endMinute, 10), 0, 0);
        this.eventForm.get('eventEndDate')?.setValue(d);
      }
    }
  }

  onRetentionTimeChange() {
    console.log('Retention time changed');
    const startDate = this.eventForm.get('eventStartDate')?.value;
    if (startDate) {
      this.updateVodExpirationFromRetention(startDate);
    } else {
      // If no start date, use today's date as reference
      const today = new Date();
      this.updateVodExpirationFromRetention(today);
    }
  }

  updateVodExpirationFromRetention(referenceDate: Date) {
    const retentionTime = this.eventForm.get('retentionTime')?.value;
    console.log('Updating VOD expiration for retention time:', retentionTime);
    
    // Create a new date based on the reference date
    const vodExpirationDate = new Date(referenceDate);
    
    // Determine how many years to add based on retention time
    let yearsToAdd = 1; // Default to 1 year
    
    switch (retentionTime) {
      case '1 Year':
        yearsToAdd = 1;
        break;
      case '2 Years':
        yearsToAdd = 2;
        break;
      case '3 Years':
        yearsToAdd = 3;
        break;
      case 'Does not need to be retained':
        yearsToAdd = 1; // Default to 1 year even if not retained
        break;
      default:
        yearsToAdd = 1;
    }
    
    vodExpirationDate.setFullYear(vodExpirationDate.getFullYear() + yearsToAdd);
    console.log('Setting VOD expiration date to:', vodExpirationDate);
    
    this.eventForm.patchValue({ vodExpirationDate: vodExpirationDate });
  }

  getDefaultVodExpirationDate(): Date {
    const today = new Date();
    const defaultExpirationDate = new Date(today);
    defaultExpirationDate.setFullYear(today.getFullYear() + 1);
    return defaultExpirationDate;
  }

  convertTo24Hour(hour: string, amPm: string): number {
    let hour24 = parseInt(hour);
    if (amPm === 'PM' && hour24 < 12) {
      hour24 += 12; // Convert PM to 24-hour format
    }
    if (amPm === 'AM' && hour24 === 12) {
      hour24 = 0; // Convert 12 AM to 0 hours
    }
    return hour24;
  }

  private getGmidFromUserPrincipalName(userPrincipalName?: string): string {
    if (!userPrincipalName) return '';
    const local = userPrincipalName.split('@')[0]?.trim();
    if (!local) return '';
    return local.substring(0, 6).toLowerCase();
  }

  private normalizeEmail(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private hasValidPersonMeta(meta: any, expectedEmail?: string): boolean {
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return false;

    const name = String(meta.name || '').trim();
    const email = String(meta.email || '').trim();
    const gmid = String(meta.gmid || '').trim();

    if (!name || !email || !gmid) return false;
    if (expectedEmail && this.normalizeEmail(expectedEmail) !== this.normalizeEmail(email)) return false;

    return true;
  }

  private getPersonMetaIssues(options: { requireEventLeader: boolean; onlyIfEntered: boolean }): string[] {
    const issues: string[] = [];

    const requestorValue = String(this.eventForm.get('requestor')?.value || '').trim();
    const requestorMeta = this.eventForm.get('requestorMeta')?.value;
    const shouldValidateRequestor = requestorValue !== '' || !options.onlyIfEntered;
    if (shouldValidateRequestor && !this.hasValidPersonMeta(requestorMeta, requestorValue)) {
      issues.push('Requestor (select from search results)');
    }

    const eventLeaderValue = String(this.eventForm.get('eventLeader')?.value || '').trim();
    const eventLeaderMeta = this.eventForm.get('eventLeaderMeta')?.value;
    const shouldValidateEventLeader = options.requireEventLeader;
    if (shouldValidateEventLeader && eventLeaderValue !== '' && !this.hasValidPersonMeta(eventLeaderMeta, eventLeaderValue)) {
      issues.push('Event Leader (select from search results)');
    }

    return issues;
  }

  private async ensurePersonMetaResolved(options: { requireEventLeader: boolean; onlyIfEntered: boolean; actionLabel: string }): Promise<boolean> {
    const issues = this.getPersonMetaIssues({
      requireEventLeader: options.requireEventLeader,
      onlyIfEntered: options.onlyIfEntered
    });
    if (issues.length === 0) return true;

    const message = `Please resolve the following before ${options.actionLabel}:\n\n${issues.join('\n')}`;
    await this._alert.error(message);
    return false;
  }

  private resolvePersonFromEmail(email: string, onResolved: (user: any) => void): void {
    if (!email) return;
    this._user.searchPerson(email).subscribe((response: any) => {
      if (!response || response.length === 0) return;

      const emailLower = email.toLowerCase();
      const exact = response.find((u: any) => (u.mail || '').toLowerCase() === emailLower)
        || response.find((u: any) => (u.userPrincipalName || '').toLowerCase() === emailLower);

      const selected = exact || (response.length === 1 ? response[0] : null);
      if (!selected) return;

      onResolved(selected);
    });
  }

  private backfillRequestorFromEmail(email: string): void {
    this.resolvePersonFromEmail(email, (user) => {
      const requestorMeta = {
        gmid: this.getGmidFromUserPrincipalName(user.userPrincipalName),
        email: user.mail || user.userPrincipalName || email,
        name: user.displayName || ''
      };
      this.eventForm.get('requestor')?.setValue(requestorMeta.email);
      this.eventForm.get('requestorMeta')?.setValue(requestorMeta);
      this.requestorName = requestorMeta.name;
      this.requestorLink = requestorMeta.gmid ? `https://peoplefinder.gm.com/person/show/${requestorMeta.gmid}` : '';
      this.isRequestorLinkVisible = !!this.requestorLink;
    });
  }

  private backfillEventLeaderFromEmail(email: string): void {
    this.resolvePersonFromEmail(email, (user) => {
      const eventLeaderMeta = {
        gmid: this.getGmidFromUserPrincipalName(user.userPrincipalName),
        email: user.mail || user.userPrincipalName || email,
        name: user.displayName || ''
      };
      this.eventForm.get('eventLeader')?.setValue(eventLeaderMeta.email);
      this.eventForm.get('eventLeaderMeta')?.setValue(eventLeaderMeta);
      this.eventLeaderName = eventLeaderMeta.name;
      this.eventLeaderLink = eventLeaderMeta.gmid ? `https://peoplefinder.gm.com/person/show/${eventLeaderMeta.gmid}` : '';
      this.isEventLeaderLinkVisible = !!this.eventLeaderLink;
    });
  }

  private backfillCaptionEditorFromEmail(email: string): void {
    this.resolvePersonFromEmail(email, (user) => {
      const captionEditorMeta = {
        gmid: this.getGmidFromUserPrincipalName(user.userPrincipalName),
        email: user.mail || user.userPrincipalName || email,
        name: user.displayName || ''
      };
      this.eventForm.get('captionEditor')?.setValue(captionEditorMeta.email);
      this.captionEditorName = captionEditorMeta.name;
      this.captionEditorLink = captionEditorMeta.gmid ? `https://peoplefinder.gm.com/person/show/${captionEditorMeta.gmid}` : '';
      this.isCaptionEditorLinkVisible = !!this.captionEditorLink;
    });
  }

  private backfillVideoEditorFromEmail(email: string): void {
    this.resolvePersonFromEmail(email, (user) => {
      const videoEditorMeta = {
        gmid: this.getGmidFromUserPrincipalName(user.userPrincipalName),
        email: user.mail || user.userPrincipalName || email,
        name: user.displayName || ''
      };
      this.eventForm.get('videoEditingAssignedTo')?.setValue(videoEditorMeta.email);
      this.videoEditorName = videoEditorMeta.name;
      this.videoEditorLink = videoEditorMeta.gmid ? `https://peoplefinder.gm.com/person/show/${videoEditorMeta.gmid}` : '';
      this.isVideoEditorLinkVisible = !!this.videoEditorLink;
    });
  }

  private initializeCaptionEditorFromStoredValue(value: any): void {
    if (!value) {
      this.isCaptionEditorLinkVisible = false;
      this.captionEditorName = '';
      this.captionEditorLink = '';
      return;
    }

    if (typeof value === 'object') {
      const email = value.email || value.mail || value.userPrincipalName || '';
      const name = value.name || value.displayName || '';
      const gmid = value.gmid || this.getGmidFromUserPrincipalName(value.userPrincipalName);
      this.eventForm.get('captionEditor')?.setValue(email || name || '');
      this.captionEditorName = name || email || '';
      this.captionEditorLink = gmid ? `https://peoplefinder.gm.com/person/show/${gmid}` : '';
      this.isCaptionEditorLinkVisible = !!this.captionEditorLink;

      if ((!gmid || !name) && email) {
        this.backfillCaptionEditorFromEmail(email);
      }
      return;
    }

    const textValue = String(value).trim();
    this.eventForm.get('captionEditor')?.setValue(textValue);
    this.captionEditorName = '';
    this.captionEditorLink = '';
    this.isCaptionEditorLinkVisible = false;

    if (textValue.includes('@')) {
      this.backfillCaptionEditorFromEmail(textValue);
    }
  }

  private initializeVideoEditorFromStoredValue(value: any): void {
    if (!value) {
      this.isVideoEditorLinkVisible = false;
      this.videoEditorName = '';
      this.videoEditorLink = '';
      return;
    }

    if (typeof value === 'object') {
      const email = value.email || value.mail || value.userPrincipalName || '';
      const name = value.name || value.displayName || '';
      const gmid = value.gmid || this.getGmidFromUserPrincipalName(value.userPrincipalName);
      this.eventForm.get('videoEditingAssignedTo')?.setValue(email || name || '');
      this.videoEditorName = name || email || '';
      this.videoEditorLink = gmid ? `https://peoplefinder.gm.com/person/show/${gmid}` : '';
      this.isVideoEditorLinkVisible = !!this.videoEditorLink;

      if ((!gmid || !name) && email) {
        this.backfillVideoEditorFromEmail(email);
      }
      return;
    }

    const textValue = String(value).trim();
    this.eventForm.get('videoEditingAssignedTo')?.setValue(textValue);
    this.videoEditorName = '';
    this.videoEditorLink = '';
    this.isVideoEditorLinkVisible = false;

    if (textValue.includes('@')) {
      this.backfillVideoEditorFromEmail(textValue);
    }
  }

  async checkParticipantCount() {
    let numPart = this.eventForm.get('expectedNumberOfParticipants')?.value;
    if (numPart < 1) {
      await this._alert.error('Number of participants must be at least 1.');
      this.eventForm.get('expectedNumberOfParticipants')?.setValue(1);
    } else if (numPart < 1000) {
      await this._alert.warning('If the number of participants is under 1000, please consider using Teams. We can still help you organize your event.');
    }
  }


  popUpWidth = "70%";
  requestorName: string = '';
  requestorPicture: string = '';
  private suppressRequestorBlur = false;
  searchRequestor() {
    const person = this.eventForm.get('requestor')?.value;
    this._user.searchPerson(person).subscribe((response: any) => {
      // console.log('Graph API Response:', response);
      if (response && response.length > 0) {
        // let gmid = '';
        if (response.length === 1) {
          // If only one user is found, set the requestor directly
          const user = response[0];
          this.requestorName = user.displayName;
          const requestorMeta = {
            gmid: this.getGmidFromUserPrincipalName(user.userPrincipalName),
            email: user.mail || user.userPrincipalName || '',
            name: user.displayName || ''
          };
          this.eventForm.get('requestor')?.setValue(requestorMeta.email);
          this.eventForm.get('requestorMeta')?.setValue(requestorMeta);
          this.requestorLink = requestorMeta.gmid ? `https://peoplefinder.gm.com/person/show/${requestorMeta.gmid}` : '';
          this.setSelectionRequiredError('requestor', false);
          this.setNoMatchError('requestor', false);
          // this._user.getOtherGraphProfilePhoto(user.id).subscribe(res => {
          //   console.log("Get graph profile", res)
          //   if (res) {
          //     this.requestorPicture = res;
          //   } else {
          //     this.requestorPicture = '';
          //   }
          // });
          this.isRequestorLinkVisible = true;
        } else {
          // If multiple users are found, open the dialog
          const dialogRef = this.dialog.open(UserSelectionDialogComponent, {
            width: this.popUpWidth,
            data: { users: response }
          });

          dialogRef.afterClosed().subscribe((selectedUser) => {
            if (selectedUser) {
              this.requestorName = selectedUser.displayName;
              const requestorMeta = {
                gmid: this.getGmidFromUserPrincipalName(selectedUser.userPrincipalName),
                email: selectedUser.mail || selectedUser.userPrincipalName || '',
                name: selectedUser.displayName || ''
              };
              this.eventForm.get('requestor')?.setValue(requestorMeta.email);
              this.eventForm.get('requestorMeta')?.setValue(requestorMeta);
              this.requestorLink = requestorMeta.gmid ? `https://peoplefinder.gm.com/person/show/${requestorMeta.gmid}` : '';
              this.setSelectionRequiredError('requestor', false);
              this.setNoMatchError('requestor', false);
              // this._user.getOtherGraphProfilePhoto(selectedUser.id).subscribe(res => {
              //   console.log("Get graph profile", res)
              //   if (res) {
              //     this.requestorPicture = res;
              //   } else {
              //     this.requestorPicture = '';
              //   }
              // });
              this.isRequestorLinkVisible = true;
            } else {
              this.setSelectionRequiredError('requestor', true);
              this.setNoMatchError('requestor', false);
            }
          });
        }
        // Get user picture from PS if available
        // this._user.getPSProfilePhoto(gmid).subscribe(res => {
        //   if (res) {
        //     this.requestorPicture = res;
        //   } else {
        //     this.requestorPicture = '';
        //   }
        // });
      } else {
        console.error('No matching user found');
        this.eventForm.get('requestorMeta')?.setValue(null);
        this.isRequestorLinkVisible = false; // Hide the link if no match is found
        this.requestorName = "";
        this.setSelectionRequiredError('requestor', false);
        this.setNoMatchError('requestor', true);
      }
    });
  }

  // Function to search for an event leader using Microsoft Graph API
  eventLeaderPicture: string = '';
  eventLeaderName: string = '';
  private suppressEventLeaderBlur = false;
  searchEventLeader() {
    const person = this.eventForm.get('eventLeader')?.value;
    this._user.searchPerson(person).subscribe((response: any) => {
      // console.log('Graph API Response:', response);
      if (response && response.length > 0) {
        if (response.length === 1) {
          // If only one user is found, set the event leader directly
          const user = response[0];
          this.eventLeaderName = user.displayName;
          console.log("Event leader user object:", user);
          const eventLeaderMeta = {
            gmid: this.getGmidFromUserPrincipalName(user.userPrincipalName),
            email: user.mail || user.userPrincipalName || '',
            name: user.displayName || ''
          };
          this.eventForm.get('eventLeader')?.setValue(eventLeaderMeta.email);
          this.eventForm.get('eventLeaderMeta')?.setValue(eventLeaderMeta);
          this.eventLeaderLink = eventLeaderMeta.gmid ? `https://peoplefinder.gm.com/person/show/${eventLeaderMeta.gmid}` : '';
          this.isEventLeaderLinkVisible = true;
          this.setSelectionRequiredError('eventLeader', false);
          this.setNoMatchError('eventLeader', false);
        } else {
          // If multiple users are found, open the dialog
          const dialogRef = this.dialog.open(UserSelectionDialogComponent, {
            width: this.popUpWidth,
            data: { users: response }
          });

          dialogRef.afterClosed().subscribe((selectedUser) => {
            if (selectedUser) {
              this.eventLeaderName = selectedUser.displayName;
              const eventLeaderMeta = {
                gmid: this.getGmidFromUserPrincipalName(selectedUser.userPrincipalName),
                email: selectedUser.mail || selectedUser.userPrincipalName || '',
                name: selectedUser.displayName || ''
              };
              this.eventForm.get('eventLeader')?.setValue(eventLeaderMeta.email);
              this.eventForm.get('eventLeaderMeta')?.setValue(eventLeaderMeta);
              this.eventLeaderLink = eventLeaderMeta.gmid ? `https://peoplefinder.gm.com/person/show/${eventLeaderMeta.gmid}` : '';
              this.isEventLeaderLinkVisible = true;
              this.setSelectionRequiredError('eventLeader', false);
              this.setNoMatchError('eventLeader', false);
            } else {
              this.setSelectionRequiredError('eventLeader', true);
              this.setNoMatchError('eventLeader', false);
            }
          });
        }
        // Get user picture from PS if available
      } else {
        console.error('No matching user found');
        this.eventForm.get('eventLeaderMeta')?.setValue(null);
        this.isEventLeaderLinkVisible = false; // Hide the link if no match is found
        this.eventLeaderName = "";
        this.setSelectionRequiredError('eventLeader', false);
        this.setNoMatchError('eventLeader', true);
      }
    });
  }

  onRequestorInputChange() {
    this.eventForm.get('requestorMeta')?.setValue(null);
    this.requestorLink = '';
    this.requestorName = '';
    this.isRequestorLinkVisible = false;
    this.setSelectionRequiredError('requestor', false);
    this.setNoMatchError('requestor', false);
  }

  onRequestorBlur() {
    if (this.isRequestorLinkVisible) {
      return;
    }
    if (this.suppressRequestorBlur) {
      this.suppressRequestorBlur = false;
      return;
    }
    const value = String(this.eventForm.get('requestor')?.value || '').trim();
    if (value) {
      this.searchRequestor();
    }
  }

  onEventLeaderInputChange() {
    this.eventForm.get('eventLeaderMeta')?.setValue(null);
    this.eventLeaderLink = '';
    this.eventLeaderName = '';
    this.isEventLeaderLinkVisible = false;
    this.setSelectionRequiredError('eventLeader', false);
    this.setNoMatchError('eventLeader', false);
  }

  private setNoMatchError(controlName: string, noMatch: boolean): void {
    const control = this.eventForm.get(controlName);
    if (!control) {
      return;
    }

    const errors = { ...(control.errors || {}) } as Record<string, boolean>;
    if (noMatch) {
      errors['noMatch'] = true;
      control.setErrors(errors);
      control.markAsTouched();
      return;
    }

    if (errors['noMatch']) {
      delete errors['noMatch'];
    }
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  onEventLeaderEnter(event: Event) {
    (event as KeyboardEvent).preventDefault();
    this.suppressEventLeaderBlur = true;
    this.searchEventLeader();
  }

  private setSelectionRequiredError(controlName: string, required: boolean): void {
    const control = this.eventForm.get(controlName);
    if (!control) {
      return;
    }

    const errors = { ...(control.errors || {}) } as Record<string, boolean>;
    if (required) {
      errors['selectionRequired'] = true;
      control.setErrors(errors);
      control.markAsTouched();
      return;
    }

    if (errors['selectionRequired']) {
      delete errors['selectionRequired'];
    }
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  onRequestorEnter(event: Event) {
    (event as KeyboardEvent).preventDefault();
    this.suppressRequestorBlur = true;
    this.searchRequestor();
  }

  onEventLeaderBlur() {
    if (this.isEventLeaderLinkVisible) {
      return;
    }
    if (this.suppressEventLeaderBlur) {
      this.suppressEventLeaderBlur = false;
      return;
    }
    const value = String(this.eventForm.get('eventLeader')?.value || '').trim();
    if (value) {
      this.searchEventLeader();
    }
  }

  searchCaptionEditor() {
    const person = this.eventForm.get('captionEditor')?.value;
    if (!person) {
      this.eventForm.get('captionEditor')?.setValue('');
      this.isCaptionEditorLinkVisible = false;
      this.captionEditorName = '';
      return;
    }
    this._user.searchPerson(person).subscribe((response: any) => {
      if (response && response.length > 0) {
        if (response.length === 1) {
          const user = response[0];
            this.captionEditorName = user.displayName;
            this.eventForm.get('captionEditor')?.setValue(user.mail);
            this.captionEditorLink = `https://peoplefinder.gm.com/person/show/${this.getGmidFromUserPrincipalName(user.userPrincipalName)}`;
            this.isCaptionEditorLinkVisible = true;
        } else {
          const dialogRef = this.dialog.open(UserSelectionDialogComponent, {
            width: this.popUpWidth,
            data: { users: response }
          });
          dialogRef.afterClosed().subscribe((selectedUser) => {
            if (selectedUser) {
              this.captionEditorName = selectedUser.displayName;
              this.eventForm.get('captionEditor')?.setValue(selectedUser.mail);
              this.captionEditorLink = `https://peoplefinder.gm.com/person/show/${this.getGmidFromUserPrincipalName(selectedUser.userPrincipalName)}`;
              this.isCaptionEditorLinkVisible = true;
            }
          });
        }
      } else {
        this.eventForm.get('captionEditor')?.setValue('');
        this.isCaptionEditorLinkVisible = false;
        this.captionEditorName = '';
      }
    });
  }

  searchVideoEditor() {
    const person = this.eventForm.get('videoEditingAssignedTo')?.value;
    if (!person) {
      this.eventForm.get('videoEditingAssignedTo')?.setValue('');
      this.isVideoEditorLinkVisible = false;
      this.videoEditorName = '';
      return;
    }
    this._user.searchPerson(person).subscribe((response: any) => {
      if (response && response.length > 0) {
        if (response.length === 1) {
          const user = response[0];
          this.videoEditorName = user.displayName;
          this.eventForm.get('videoEditingAssignedTo')?.setValue(user.mail);
          this.videoEditorLink = `https://peoplefinder.gm.com/person/show/${this.getGmidFromUserPrincipalName(user.userPrincipalName)}`;
          this.isVideoEditorLinkVisible = true;
        } else {
          const dialogRef = this.dialog.open(UserSelectionDialogComponent, {
            width: this.popUpWidth,
            data: { users: response }
          });
          dialogRef.afterClosed().subscribe((selectedUser) => {
            if (selectedUser) {
              this.videoEditorName = selectedUser.displayName;
              this.eventForm.get('videoEditingAssignedTo')?.setValue(selectedUser.mail);
              this.videoEditorLink = `https://peoplefinder.gm.com/person/show/${this.getGmidFromUserPrincipalName(selectedUser.userPrincipalName)}`;
              this.isVideoEditorLinkVisible = true;
            }
          });
        }
      } else {
        this.eventForm.get('videoEditingAssignedTo')?.setValue('');
        this.isVideoEditorLinkVisible = false;
        this.videoEditorName = '';
      }
    });
  }

  clearCaptionEditor() {
    this.eventForm.get('captionEditor')?.setValue('');
    this.captionEditorLink = '';
    this.captionEditorName = '';
    this.isCaptionEditorLinkVisible = false;
  }

  clearVideoEditor() {
    this.eventForm.get('videoEditingAssignedTo')?.setValue('');
    this.videoEditorLink = '';
    this.videoEditorName = '';
    this.isVideoEditorLinkVisible = false;
  }

  // Function to clear the requestor field and switch back to the input field
  clearRequestor() {
    this.eventForm.get('requestor')?.setValue(''); // Clear the form control
    this.eventForm.get('requestorMeta')?.setValue(null);
    this.requestorLink = ''; // Clear the hyperlink
    this.requestorName = '';
    this.isRequestorLinkVisible = false; // Show the input field
  }

  // Function to clear the event leader field and switch back to the input field
  clearEventLeader() {
    this.eventForm.get('eventLeader')?.setValue(''); // Clear the form control
    this.eventForm.get('eventLeaderMeta')?.setValue(null);
    this.eventLeaderLink = ''; // Clear the hyperlink
    this.eventLeaderName = '';
    this.isEventLeaderLinkVisible = false; // Show the input field
  }

  // @HostListener('document:keydown.enter', ['$event'])
  // handleEnterKey(event: KeyboardEvent): void {
  //   const target = event.target as HTMLElement;
  //   event.preventDefault()
  //   // if (target.id === 'requestor') {
  //   //   this.searchRequestor();
  //   // } else if (target.id === 'eventLeader') {
  //   //   this.searchEventLeader();
  //   // } 
  //   // else if (target.id === 'captionEditor') {
  //   //   this.searchCaptionEditor();
  //   // } else if (target.id === 'videoEditingAssignedTo') {
  //   //   this.searchVideoEditor();
  //   // }
  // }

  async saveForLater(): Promise<void> {
    const requestType = this.eventForm.get('requestType')?.value;
    const requireEventLeader = ['Live Event', 'Recording', 'Special Request'].includes(requestType);
    const canProceed = await this.ensurePersonMetaResolved({
      requireEventLeader,
      onlyIfEntered: false,
      actionLabel: 'saving'
    });
    if (!canProceed) return;

    this.spinnerMessage = 'Saving event...';
    this.isLoading = true;
    this.eventForm.value.requestStatus = 'Unsubmitted';
    // If the start date, end date, or vod expiration date are empty, set them to null to avoid issues
    if (!this.eventForm.value.eventStartDate) {
      this.eventForm.value.eventStartDate = null;
    }
    if (!this.eventForm.value.eventEndDate) {
      this.eventForm.value.eventEndDate = null;
    }
    if (!this.eventForm.value.vodExpirationDate) {
      this.eventForm.value.vodExpirationDate = null;
    }


    const payload = this._db.generateEventPayload(this.eventForm.value, 'New'); // Generate the payload for saving
    console.log('Payload for saving:', payload);
    //send to the db
    if (!this.creatingNewEvent) {
      // Are these 2 lines necessary? Should have been done in payload creation?
      // payload.event_Id = this.eventForm.value.event_id; 
      // payload.calendar_id = this.eventForm.value.calendar_id;
      // Code may be nearly identicle to below in submit, consider abstracting
      payload.user = { name: this.user.displayName };
      this._db.updateEventById(this.eventForm.value.event_id, payload).subscribe({
        next: async (response) => {
          console.log('Event updated successfully:', response);
          this.isLoading = false; // Hide the spinner
            await this._alert.warning('Your event has been saved, but NOT submitted. To send your event request to the Broadcast team for review, you must click **Submit**.');
      const changes = this.recordVersionHistoryIfChanged();
      if (changes.length) {
        console.debug('[history] draft save changes', changes);
        if (!this.serverAutoCapturesHistory && this.eventForm.value.event_id) {
          this._db.postEventHistory(this.eventForm.value.event_id, changes).subscribe();
        }
      }
        },
        error: async (error) => {
          console.error('Error updating event:', error);
          await this._alert.error('Error updating event. Please try again later.', this._alert.getErrorDetails(error));
        }
      });
    } else {
      this._db.createEvent(payload).subscribe({
        next: async (response) => {
          console.log('Event created successfully:', response);
          this.isLoading = false; // Hide the spinner
          await this._alert.warning('Event has been saved for later. This will NOT submit your event request to the Broadcast team for review.');
          // After create we have no previous history; snapshot current
          this.originalEventSnapshot = { ...this.eventForm.value };
        },
        error: async (error) => {
          console.error('Error saving event:', error);
          await this._alert.error('Error saving event. Please try again later.', this._alert.getErrorDetails(error));
        }
      });
    }
  }

  async onSubmit() {
    this.allMultifieldUpdate();
    // Error checking before submit (includes date/time validation)
    const canSubmit = await this.readyToSubmit();
    if (!canSubmit) return;

    console.log('Form submitted:', this.eventForm.value);
    // if (this.eventForm.valid) {
    this.isLoading = true;
    let payload = this._db.generateEventPayload(this.eventForm.value, this.eventForm.value.workflow);
    console.log('Payload for submission:', payload);

    // If already exists, update. If not, create new
    if (!this.creatingNewEvent) {
      payload.user = { name: this.user.displayName };
      this._db.updateEventById(this.eventForm.value.event_id, payload).subscribe({
        next: (response) => {
          this.submitSuccess(response, this.eventForm.value.event_id);
        },
        error: async (error) => {
          console.error('Error updating event:', error);
          this.isLoading = false;
          await this._alert.error('Error updating event. Please try again later.', this._alert.getErrorDetails(error));
        }
      });
    } else {
      // Set Default Studio Needs, starting 1 hour before the event and ending 5 minutes before
      payload.studio_needs = `${new Date(new Date(payload.start_date).getTime() - 60 * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(new Date(payload.start_date).getTime() - 5 * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - Set up and test\n${new Date(new Date(payload.start_date).getTime() - 5 * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(payload.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - CD/Disclaimer`;
      // console.log('Default studio needs set to:', payload.studio_needs);
      payload.workflow = 'New -> Submitted';
      this._db.createEvent(payload).subscribe({
        next: async (response) => {
          this.submitSuccess(response, response.request.event_id);
          // route to new event ID
          this.router.navigate(['/request'], { queryParams: { id: response.request.event_id } });
        },
        error: async (error) => {
          console.error('Error submitting event:', error);
          this.isLoading = false;
          await this._alert.error('Error submitting event. Please try again later.', this._alert.getErrorDetails(error));
        }
      });
    }
  }

  /**
   * Returns true or false depending on if all required fields have been filled out, and also outputs the field names that are not filled out
   */
  async readyToSubmit(): Promise<boolean> {
    if (this.eventForm.get('requestTitle')?.value.includes(' - Cloned from #')) {
      await this._alert.error('Please change the event title before submitting. It currently indicates that it is a cloned event.');
      console.error('Event title indicates it is a cloned event.');
      return false;
    }

    // Check date/time fields completeness first
    const startDate = this.eventForm.get('eventStartDate')?.value;
    const endDate = this.eventForm.get('eventEndDate')?.value;
    const startHour = this.eventForm.get('startHour')?.value;
    const startMinute = this.eventForm.get('startMinute')?.value;
    const endHour = this.eventForm.get('endHour')?.value;
    const endMinute = this.eventForm.get('endMinute')?.value;
    
    console.log('=== Date/Time Fields Check ===');
    console.log('Start Date:', startDate, 'Type:', typeof startDate, 'JSON:', JSON.stringify(startDate));
    console.log('Start Hour:', startHour, 'Type:', typeof startHour, 'JSON:', JSON.stringify(startHour));
    console.log('Start Minute:', startMinute, 'Type:', typeof startMinute, 'JSON:', JSON.stringify(startMinute));
    console.log('End Date:', endDate, 'Type:', typeof endDate, 'JSON:', JSON.stringify(endDate));
    console.log('End Hour:', endHour, 'Type:', typeof endHour, 'JSON:', JSON.stringify(endHour));
    console.log('End Minute:', endMinute, 'Type:', typeof endMinute, 'JSON:', JSON.stringify(endMinute));
    
    const missingDateTimeFields: string[] = [];
    
    // Check all date/time fields and collect missing ones
    if (!startDate || startDate === '' || startDate === null || startDate === undefined) {
      console.log('Start Date is missing');
      missingDateTimeFields.push('Start Date');
    }
    
    if (!endDate || endDate === '' || endDate === null || endDate === undefined) {
      console.log('End Date is missing');
      missingDateTimeFields.push('End Date');
    }
    
    // For hours: check if empty, null, undefined
    // Note: 0 is a valid hour (midnight), so we can't treat it as invalid
    // BUT if user hasn't interacted with the dropdown, it should be empty string from placeholder
    if (startHour === '' || startHour === null || startHour === undefined) {
      console.log('Start Hour is missing:', startHour);
      missingDateTimeFields.push('Start Hour');
    }
    
    if (startMinute === '' || startMinute === null || startMinute === undefined) {
      console.log('Start Minute is missing:', startMinute);
      missingDateTimeFields.push('Start Minute');
    }
    
    if (endHour === '' || endHour === null || endHour === undefined) {
      console.log('End Hour is missing:', endHour);
      missingDateTimeFields.push('End Hour');
    }
    
    if (endMinute === '' || endMinute === null || endMinute === undefined) {
      console.log('End Minute is missing:', endMinute);
      missingDateTimeFields.push('End Minute');
    }
    
    console.log('Missing fields array:', missingDateTimeFields);
    
    // If any date/time fields are missing, show single popup with all missing fields
    if (missingDateTimeFields.length > 0) {
      const message = `Please fill out the following required date/time fields:\n\n${missingDateTimeFields.join('\n')}`;
      await this._alert.warning(message);
      return false;
    }

    this.missingFields = [];
    const requestType = this.eventForm.get('requestType')?.value;

    // Common fields for all request types
    const commonRequiredFields = [
      { field: 'requestTitle', name: 'Request Title' },
      { field: 'eventStartDate', name: 'Event Start Date' },
      { field: 'eventEndDate', name: 'Event End Date' },
      { field: 'startHour', name: 'Start Hour' },
      { field: 'startMinute', name: 'Start Minute' },
      { field: 'endHour', name: 'End Hour' },
      { field: 'endMinute', name: 'End Minute' },
      { field: 'requestor', name: 'Requestor' }
    ];

    // Check common fields
    commonRequiredFields.forEach(({ field, name }) => {
      const value = this.eventForm.get(field)?.value;
      // Special handling for numeric fields that can be 0 (like hours)
      if (field === 'startHour' || field === 'endHour') {
        if (value === null || value === undefined || value === '') {
          console.log(`Missing field detected: ${name} = ${value}`);
          this.missingFields.push(name);
        }
      } else {
        // Standard validation for non-numeric fields
        if (!value || value === '') {
          console.log(`Missing field detected: ${name} = ${value}`);
          this.missingFields.push(name);
        }
      }
    });

    // Request type specific validation
    switch (requestType) {
      case 'Live Event':
        this.validateLiveEventFields();
        break;
      case 'Recording':
        this.validateRecordingFields();
        break;
      case 'Special Request':
        this.validateSpecialRequestFields();
        break;
      case 'Holiday/Vacation/Internal Testing':
        this.validateHolidayFields();
        break;
      default:
        this.missingFields.push('Request Type');
    }

    const personMetaIssues = this.getPersonMetaIssues({
      requireEventLeader: ['Live Event', 'Recording', 'Special Request'].includes(requestType),
      onlyIfEntered: false
    });
    personMetaIssues.forEach((issue) => {
      if (!this.missingFields.includes(issue)) {
        this.missingFields.push(issue);
      }
    });

    // If there are missing fields, show error and return false
    if (this.missingFields.length > 0) {
      const errorMessage = `Please fill out the following required fields:\n\n${this.missingFields.join('\n')}`;
      await this._alert.error(errorMessage);
      console.error('Missing required fields:', this.missingFields);
      return false;
    }

    return true;
  }

  /**
   * Validates required fields for Live Event request type
   */
  private validateLiveEventFields(): void {
    const liveEventFields = [
      { field: 'expectedNumberOfParticipants', name: 'Expected Number of Participants' },
      { field: 'primaryPresentingLocation', name: 'Primary Presenting Location' },
      { field: 'eventLeader', name: 'Event Leader' },
      { field: 'eventRequirements', name: 'Event Requirements' },
      { field: 'region', name: 'Region' },
      { field: 'dataClassification', name: 'Data Classification' },
      { field: 'retentionTime', name: 'Retention Time' }
    ];

    liveEventFields.forEach(({ field, name }) => {
      const value = this.eventForm.get(field)?.value;
      if (!value || value === '') {
        this.missingFields.push(name);
      }
    });

    // Special validation for fields with "Specify your own value" options
    this.validateFieldWithCustomOption('audience', 'Audience', 'audSpecifyYourOwnValue', 'audienceOther');
    this.validateFieldWithCustomOption('takeQuestions', 'How Will You Take Questions', 'SpecifyYourOwnValueQuestions', 'takeQuestionsOther');

    // Check VOD expiration if VOD is needed
    const isVodNeeded = this.eventForm.get('isVideoOnDemandNeeded')?.value;
    if (isVodNeeded === 'Yes') {
      const vodExpiration = this.eventForm.get('vodExpirationDate')?.value;
      if (!vodExpiration) {
        this.missingFields.push('VOD Expiration Date');
      }
    }

    // Check custom location if "I don't see my location" is selected
    const customLocationEnabled = this.eventForm.get('SpecifyYouOwnValuePresentingLocation')?.value;
    if (customLocationEnabled) {
      const customLocation = this.eventForm.get('otherLocation')?.value;
      if (!customLocation || customLocation === '') {
        this.missingFields.push('Custom Primary Presenting Location');
      }
    }
  }

  /**
   * Validates required fields for Recording request type
   */
  private validateRecordingFields(): void {
    const recordingFields = [
      { field: 'eventLeader', name: 'Event Leader' },
      { field: 'videoSharingPlatforms', name: 'Video Sharing Platforms' },
      { field: 'region', name: 'Region' },
      { field: 'vodExpirationDate', name: 'VOD Expiration Date' },
      { field: 'dataClassification', name: 'Data Classification' },
      { field: 'retentionTime', name: 'Retention Time' }
    ];

    recordingFields.forEach(({ field, name }) => {
      const value = this.eventForm.get(field)?.value;
      if (!value || value === '') {
        this.missingFields.push(name);
      }
    });

    // Special validation for fields with "Specify your own value" options
    this.validateFieldWithCustomOption('recordingLocation', 'Recording Location', 'SpecifyYourOwnValueRecordingLocation', 'otherRecordingLocation');
    this.validateFieldWithCustomOption('videoSharingPlatforms', 'Video Sharing Platforms', 'SpecifyYourOwnValueVideoSharingPlatforms', 'videoSharingPlatformsOther');
    this.validateFieldWithCustomOption('responsibleForEditing', 'Responsible For Editing', 'SpecifyYourOwnValueResponsibleForEditing', 'responsibleForEditingOther');

    // Check custom recording location if needed
    const customRecordingEnabled = this.eventForm.get('SpecifyYourOwnValueRecordingLocation')?.value;
    if (customRecordingEnabled) {
      const customRecording = this.eventForm.get('otherRecordingLocation')?.value;
      if (!customRecording || customRecording === '') {
        this.missingFields.push('Custom Recording Location');
      }
    }
  }

  /**
   * Validates required fields for Special Request type
   */
  private validateSpecialRequestFields(): void {
    const specialRequestFields = [
      { field: 'eventLeader', name: 'Event Leader' }
    ];

    specialRequestFields.forEach(({ field, name }) => {
      const value = this.eventForm.get(field)?.value;
      if (!value || value === '') {
        this.missingFields.push(name);
      }
    });
  }

  /**
   * Validates required fields for Holiday request type
   */
  private validateHolidayFields(): void {
    // Holiday only requires common fields (title, dates, requestor)
    // No additional fields needed beyond what's already checked in common fields
  }

  /**
   * Helper method to validate fields that have "Specify your own value" option
   * @param mainField The main field name (e.g., 'audience', 'takeQuestions')
   * @param fieldLabel The human-readable field name for error messages
   * @param customCheckboxField The checkbox field name (e.g., 'audSpecifyYourOwnValue')
   * @param customTextField The text input field name (e.g., 'audienceOther')
   */
  private validateFieldWithCustomOption(mainField: string, fieldLabel: string, customCheckboxField: string, customTextField: string): void {
    const mainValue = this.eventForm.get(mainField)?.value;
    const isCustomSelected = this.eventForm.get(customCheckboxField)?.value;
    const customValue = this.eventForm.get(customTextField)?.value;

    // Field is valid if:
    // 1. Main field has a value from dropdown, OR
    // 2. Custom checkbox is checked AND custom text field has a value
    const isValid = (mainValue && mainValue !== '') || (isCustomSelected && customValue && customValue !== '');

    if (!isValid) {
      this.missingFields.push(fieldLabel);
    }
  }

  submitSuccess(response: any, event_id: any) {
    console.log('Event updated successfully:', response);
    this.disableAllFields();
    this.creatingNewEvent = false;
    this.pastSubmitted = true;
    const changes = this.recordVersionHistoryIfChanged();
    if (changes.length) {
      console.debug('[history] submit changes', changes);
      if (!this.serverAutoCapturesHistory && this.eventForm.value.event_id) {
        this._db.postEventHistory(event_id, changes).subscribe();
      }
    }
    if (!this.eventForm.value.workflow.includes('Submitted')) {
      this.isLoading = true;
      this.eventForm.get('workflow')?.setValue('New -> Submitted');
      this.eventForm.get('requestStatus')?.setValue('Pending');
      this.cachedForm = this.eventForm.value;
      // Send email
      let payload = this._db.generateEventPayload(this.eventForm.value, this.eventForm.value.workflow);
      if (!payload.event_id && event_id) {
        payload.event_id = event_id;
      }
      payload.user = { name: this.user.displayName };
      this._graph.sendSubmissionMail(payload).subscribe({
        next: async (emailResponse) => {
          this.isLoading = false;
          await this._alert.success('Event submitted successfully. An email has been sent to the broadcast team and you will hear back later.');
          // Reload
          window.location.reload();
        },
        error: async (emailError) => {
          console.error('Error sending submission email:', emailError);
          this.isLoading = false;
          await this._alert.error('Event submitted, but there was an error sending the confirmation email to the broadcast team! Please contact them directly to let them know.', this._alert.getErrorDetails(emailError));
        }
      });
    }

    this.isLoading = false;
  }

  /**
   * Compute differences between original snapshot and current form + send to backend.
   */
  private recordVersionHistoryIfChanged(): any[] {
    // Ensure we have a baseline snapshot first
    if (!this.originalEventSnapshot) {
      this.originalEventSnapshot = { ...this.eventForm.value };
      return [];
    }

    // An event id is required to associate history rows; if missing just refresh snapshot
    if (!this.eventForm.value.event_id) {
      this.originalEventSnapshot = { ...this.eventForm.value };
      return [];
    }

  const current = { ...this.eventForm.value };
  const auditUser = this.resolveAuditUser();
  const changes: any[] = [];
    for (const key of Object.keys(current)) {
      if (this.historyIgnoreFields.has(key)) continue;
      const before = this.normalizeValue(this.originalEventSnapshot[key]);
      const after = this.normalizeValue(current[key]);
      if (before !== after) {
        changes.push({
          field: key,
          fieldLabel: this.fieldLabels[key] || key,
          oldValue: before,
          newValue: after,
      user: auditUser
        });
      }
    }

    // Always advance snapshot so subsequent edits diff from latest persisted state
    this.originalEventSnapshot = { ...current };
    return changes;
  }

  /** Prefer GMID for audit user; fallback to mail/userPrincipalName local part, then name */
  private resolveAuditUser(): string {
    const u: any = this.user || {};
    if (u.gmid) return String(u.gmid);
    const upn = u.gmid ? null : (u.userPrincipalName || u.mail || u.email);
    if (upn && typeof upn === 'string') {
      const local = upn.split('@')[0];
      if (local) return local;
    }
    if (u.name) return String(u.name).replace(/\s+/g, '');
    return 'unknown';
  }

  private normalizeValue(val: any): any {
    if (val instanceof Date) return val.toISOString();
    if (val === undefined || val === null) return '';
    // Normalize booleans to string for stable comparison / storage
    if (typeof val === 'boolean') return val ? 'true' : 'false';
    // Normalize numbers (including numeric strings) to canonical string to avoid 1 vs '1' mismatches
    if (typeof val === 'number') return Number.isNaN(val) ? '' : val.toString();
    if (typeof val === 'string') {
      // Trim to avoid whitespace-only diffs
      const trimmed = val.trim();
      // If string represents a number, normalize it so '01', '1', 1 become '1'
      if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
        const num = Number(trimmed);
        if (!Number.isNaN(num)) return num.toString();
      }
      return trimmed;
    }
    // Arrays: sort + stringify for order-insensitive comparison (shallow)
    if (Array.isArray(val)) {
      try {
        const copy = [...val];
        // If primitive array, sort for deterministic diff; otherwise leave order
        if (copy.every(v => ['string','number','boolean'].includes(typeof v))) {
          return copy.map(v => this.normalizeValue(v)).sort().join('|');
        }
        return JSON.stringify(copy);
      } catch { return JSON.stringify(val); }
    }
    // Objects: stable stringify (keys sorted) to avoid key order diffs
    if (typeof val === 'object') {
      try {
        const stable = (o: any): any => {
          if (o === null) return null;
          if (Array.isArray(o)) return o.map(i => stable(i));
          if (typeof o === 'object') {
            return Object.keys(o).sort().reduce((acc, k) => {
              acc[k] = stable(o[k]);
              return acc;
            }, {} as any);
          }
          return o;
        };
        return JSON.stringify(stable(val));
      } catch { return JSON.stringify(val); }
    }
    return val;
  }

  openVersionHistory() {
    if (!this.eventForm.value.event_id) return;
    this.dialog.open(EventVersionHistoryDialogComponent, {
      // Moderated width: wide enough for emails, not overwhelming
      width: '75vw',
      maxWidth: '1200px',
      data: {
        eventId: this.eventForm.value.event_id,
        loader: () => new Promise<EventChangeRecord[]>((resolve, reject) => {
          this._db.getEventHistory(this.eventForm.value.event_id!).subscribe({
            next: (res: any) => {
              console.log("Version History Retrieved", res);
              // New API returns { success, event_id, history:[ ... ], pageInfo }
              const rows = Array.isArray(res) ? res : (res?.history || []);
              const eventId = this.eventForm.getRawValue().event_id;
              const arr = rows.map((h: any) => ({
                timestamp: h.changed_at || h.created_at || h.createdAt || h.timestamp || null,
                event_id: h.event_id || eventId,
                user: h.gmid || h.changed_by || h.modified_by || h.modifiedBy || h.user || h.email || 'unknown',
                field: h.field,
                fieldLabel: h.fieldLabel || this.fieldLabels[h.field] || h.field,
                oldValue: h.oldValue ?? null,
                newValue: h.newValue ?? null,
              }));
              
              resolve(arr);
            },
            error: (err) => {
              if (err?.status === 404) {
                // No history yet
                resolve([]);
              } else {
                reject(err);
              }
            }
          });
        })
      }
    });
  }

  // For copywrite pop up
  agreeToTerms() {
    this.showAgreement = false;
  }
  cancelAgreement() {
    this.router.navigate(['/calendar']);
  }

  /**
   * Cleans the options string by removing specified values.
   * @param optionString The string containing options separated by commas.
   * @param valuesToRemove An array of values to remove from the options string.
   * @returns A cleaned string with specified values removed.
   */
  cleanOwnOptionsString(optionString: string, valuesToRemove: string[]) {
    if (!optionString) return '';

    const cleanedValues = optionString
      .split(',')
      .map(value => value.trim())
      .filter(value => !valuesToRemove.includes(value))
      .filter(value => value !== '');

    return cleanedValues.join(', ');
  }

  /**
   * Updates all multi-select fields in the form based on selected options.
   * This method is called after the form is initialized or when options change.
   * Also refreshes date/time fields to ensure proper formatting and values.
   */
  allMultifieldUpdate() {
    // FIRST: Update date/time fields to ensure proper formatting and values BEFORE validation
    this.refreshDateTimeFields();
    
    // THEN: Update multi-select fields
    this.updateMultiField(this.requirementOptions, 'eventRequirements');
    this.updateMultiField(this.audienceOptions, 'audience', 'audSpecifyYourOwnValue', 'audienceOther');
    this.updateMultiField(this.regionOptions, 'region');
    this.updateMultiField(this.howTakeQuestionOptions, 'takeQuestions', 'SpecifyYourOwnValueQuestions', 'takeQuestionsOther');
    this.updateMultiField(this.toolsOptions, 'toolsToBeUsed', 'SpecifyYourOwnValueTools', 'toolsOther');
    this.updateMultiField(this.responsibleForEditingOptions, 'responsibleForEditing', 'SpecifyYourOwnValueResponsibleForEditing', 'responsibleForEditingOther');
    this.updateMultiField(this.videoSharingPlatformOptions, 'videoSharingPlatforms', 'SpecifyYourOwnValueVideoSharingPlatforms', 'videoSharingPlatformsOther');
  }

  /**
   * Refreshes date and time fields to ensure proper formatting and values.
   * This helps resolve issues where date/time fields become invisible or lose their values.
   */
  refreshDateTimeFields() {
    console.log('Refreshing date/time fields...');
    
    const startDate = this.eventForm.get('eventStartDate')?.value;
    const endDate = this.eventForm.get('eventEndDate')?.value;
    const vodDate = this.eventForm.get('vodExpirationDate')?.value;
    const startHour = this.eventForm.get('startHour')?.value;
    const startMinute = this.eventForm.get('startMinute')?.value;
    const endHour = this.eventForm.get('endHour')?.value;
    const endMinute = this.eventForm.get('endMinute')?.value;

    // Helper function to safely parse dates
    const parseDate = (dateValue: any): Date | null => {
      if (!dateValue) return null;
      
      try {
        let dateObj: Date;
        if (dateValue instanceof Date) {
          dateObj = new Date(dateValue);
        } else if (typeof dateValue === 'string') {
          dateObj = new Date(dateValue);
        } else {
          dateObj = dateValue;
        }
        
        return (dateObj && !isNaN(dateObj.getTime())) ? dateObj : null;
      } catch (error) {
        console.warn('Error parsing date:', dateValue, error);
        return null;
      }
    };

    // Refresh start date and time if they exist
    if (startDate) {
      const dateObj = parseDate(startDate);
      if (dateObj) {
        // Only update hour/minute if they already have valid values - don't extract from date
        // This prevents auto-filling with midnight (00:00) when user hasn't selected a time
        const patchData: any = { eventStartDate: dateObj };
        
        if (startHour !== '' && startHour !== null && startHour !== undefined) {
          patchData.startHour = parseInt(startHour);
        }
        if (startMinute !== '' && startMinute !== null && startMinute !== undefined) {
          patchData.startMinute = startMinute;
        }
        
        this.eventForm.patchValue(patchData, { emitEvent: false });
        
        console.log('Refreshed start date:', dateObj, 'hour:', patchData.startHour || 'not set', 'minute:', patchData.startMinute || 'not set');
      }
    }

    // Refresh end date and time if they exist
    if (endDate) {
      const dateObj = parseDate(endDate);
      if (dateObj) {
        // Only update hour/minute if they already have valid values - don't extract from date
        // This prevents auto-filling with midnight (00:00) when user hasn't selected a time
        const patchData: any = { eventEndDate: dateObj };
        
        if (endHour !== '' && endHour !== null && endHour !== undefined) {
          patchData.endHour = parseInt(endHour);
        }
        if (endMinute !== '' && endMinute !== null && endMinute !== undefined) {
          patchData.endMinute = endMinute;
        }
        
        this.eventForm.patchValue(patchData, { emitEvent: false });
        
        console.log('Refreshed end date:', dateObj, 'hour:', patchData.endHour || 'not set', 'minute:', patchData.endMinute || 'not set');
      }
    }

    // Refresh VOD expiration date if it exists
    if (vodDate) {
      const dateObj = parseDate(vodDate);
      if (dateObj) {
        this.eventForm.patchValue({
          vodExpirationDate: dateObj
        }, { emitEvent: false });
        
        console.log('Refreshed VOD expiration date:', dateObj);
      }
    }

    // Log final state for debugging
    console.log('Date/time fields refresh completed. Final values:', {
      startDate: this.eventForm.get('eventStartDate')?.value,
      startHour: this.eventForm.get('startHour')?.value,
      startMinute: this.eventForm.get('startMinute')?.value,
      endDate: this.eventForm.get('eventEndDate')?.value,
      endHour: this.eventForm.get('endHour')?.value,
      endMinute: this.eventForm.get('endMinute')?.value
    });
  }

  /**
   * Ensures that if date fields exist, the corresponding hour/minute fields are not empty.
   * This is a final safety check before form validation.
   */
  /**
   * Updates a multi-select field in the form based on selected options.
   * @param optionsOptions An dictionary of option key value pairs to check in the form.
   * @param fieldFormName The name of the form control to update with selected options.
   * @param customEntryBoolName Optional. The name of the boolean control for custom entry.
   * @param customEntryFieldName Optional. The name of the form control for custom entry text.
   */
  updateMultiField(optionsObject: { [key: string]: string }, fieldFormName: string, customEntryBoolName?: string, customEntryFieldName?: string) {
    // Get selected option keys where the form control value is true
    const selectedOptionKeys = Object.keys(optionsObject).filter(key => this.eventForm.get(key)?.value);

    // Update the main field with comma-separated values
    this.eventForm.get(fieldFormName)?.setValue(selectedOptionKeys.join(', '));

    // Clear custom entry if the boolean is false
    if (customEntryBoolName && !this.eventForm.get(customEntryBoolName)?.value && customEntryFieldName) {
      this.eventForm.get(customEntryFieldName)?.setValue('');
    }
  }

  /**
   * Helper method to convert options object to array for *ngFor
   */
  getOptionEntries(optionsObject: { [key: string]: string }): Array<{ key: string, value: string }> {
    if (!optionsObject) return [];
    return Object.entries(optionsObject).map(([key, value]) => ({ key, value }));
  }

  trackByOptionKey(index: number, option: { key: string, value: string }): string {
    return option.key;
  }

  trackByToolGroup(index: number, group: { parent: { key: string; value: string } }): string {
    return group.parent.key;
  }

  /**
   * Determines if a tool option should be indented
   * @param toolKey The key of the tool option
   * @returns true if the tool should be indented
   */
  isIndentedTool(toolKey: string): boolean {
    return this.getToolParentKey(toolKey) !== null;
  }

  private getToolParentKey(toolKey: string): string | null {
    const parentMap: Record<string, string> = {
      toolTeamPolls: 'toolTEAMS',
      toolTeamsBreakoutRooms: 'toolTEAMS',
      toolWebcast: 'toolVirtualEvents',
      toolDIYWebcast: 'toolVirtualEvents',
      toolPreRecordedSimulated: 'toolVirtualEvents',
      toolInteractiveSessions: 'toolVirtualEvents'
    };
    return parentMap[toolKey] ?? null;
  }

  isIndentedToolParent(index: number): boolean {
    const entries = this.getOptionEntries(this.toolsOptions);
    const next = entries[index + 1];
    return !!next && this.isIndentedTool(next.key);
  }

  private buildToolGroups(optionsObject: { [key: string]: string }): Array<{ parent: { key: string; value: string }; children: Array<{ key: string; value: string }> }> {
    const entries = this.getOptionEntries(optionsObject);
    const groups: Array<{ parent: { key: string; value: string }; children: Array<{ key: string; value: string }> }> = [];
    const groupsByKey = new Map<string, { parent: { key: string; value: string }; children: Array<{ key: string; value: string }> }>();

    for (const entry of entries) {
      const parentKey = this.getToolParentKey(entry.key);
      if (parentKey) {
        let group = groupsByKey.get(parentKey);
        if (!group) {
          group = { parent: { key: parentKey, value: parentKey }, children: [] };
          groupsByKey.set(parentKey, group);
          groups.push(group);
        }
        group.children.push(entry);
        continue;
      }

      let group = groupsByKey.get(entry.key);
      if (!group) {
        group = { parent: entry, children: [] };
        groupsByKey.set(entry.key, group);
        groups.push(group);
      } else {
        group.parent = entry;
      }
    }

    return groups;
  }

  /**
   * Handles changes to the presenting location dropdown.
   * If "I don't see my location" is selected, shows the custom input field.
   * Otherwise, hides the custom input field and clears its value.
   * @param event The change event from the dropdown
   */
  onPrimaryLocationChange(event: any): void {
    const selectedValue = event.target.value;
    if (selectedValue === "I don't see my location") {
      this.eventForm.get('SpecifyYouOwnValuePresentingLocation')?.setValue(true);
    } else {
      this.eventForm.get('SpecifyYouOwnValuePresentingLocation')?.setValue(false);
      this.eventForm.get('otherLocation')?.setValue('');
    }
  }

  onRecordingLocationChange(event: any): void {
    const selectedValue = event.target.value;
    console.log('Recording location changed to:', selectedValue);
    if (selectedValue === "I don't see my location") {
      this.eventForm.get('SpecifyYourOwnValueRecordingLocation')?.setValue(true);
    } else {
      this.eventForm.get('SpecifyYourOwnValueRecordingLocation')?.setValue(false);
      this.eventForm.get('otherRecordingLocation')?.setValue('');
    }
  }

  /**
   * Opens a given URL in a new browser tab.
   * If the URL does not start with "http://" or "https://", "https://" is prepended.
   * @param url The URL to open
   */
  openLinkInNewTab(url: string): void {
    if (!url) {
      console.log('No URL provided');
      return;
    }

    // Ensure the URL has a protocol
    let fullUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      fullUrl = 'https://' + url;
    }

    window.open(fullUrl, '_blank');
  }

  /**
   * Disables all form fields to make the form read-only.
   * Also sets isEditing to false to update the UI accordingly.
   */
  disableAllFields() {
    this.isEditing = false;
    Object.keys(this.eventForm.controls).forEach(key => {
      this.eventForm.get(key)?.disable();
    });
  }

  /**
   * Enables all form fields to allow editing.
   * Caches the current form value for change detection.
   * Also sets isEditing to true to update the UI accordingly.
   */
  enableAllFields() {
    this.cachedForm = { ...this.eventForm.value };
    this.isEditing = true;
    Object.keys(this.eventForm.controls).forEach(key => {
      this.eventForm.get(key)?.enable();
    });
    
    // Refresh date/time fields when entering edit mode to ensure they display properly
    this.refreshDateTimeFields();
  }

  /**
   * Compares the current form value with the cached form value to determine if there are unsaved changes.
   * @returns true if the forms are identical, false if there are changes
   */
  compareForms(): boolean {
    return JSON.stringify(this.cachedForm) === JSON.stringify(this.eventForm.value);
  }

  async sendToSchedule() {
    const requestType = this.eventForm.get('requestType')?.value;
    const requireEventLeader = ['Live Event', 'Recording', 'Special Request'].includes(requestType);
    const canProceed = await this.ensurePersonMetaResolved({
      requireEventLeader,
      onlyIfEntered: false,
      actionLabel: 'sending to schedule'
    });
    if (!canProceed) return;

    // Validate required fields before sending to schedule
    const missingFields = this.validateSendToScheduleFields();
    if (missingFields.length > 0) {
      const errorMessage = `Please fill out the following required fields before sending to schedule:\n\n${missingFields.join('\n')}`;
      await this._alert.error(errorMessage);
      return;
    }

    this.isLoading = true;
    this.eventForm.get('workflow')?.setValue(this.eventForm.value.workflow + ' -> SentToSchedule');
    this.cachedForm = { ...this.eventForm.value };

    let payload = this._db.generateEventPayload(this.eventForm.value, this.eventForm.value.workflow);
    payload.user = { name: this.user.displayName };

    this._graph.sendToScheduleMail(payload).subscribe({
      next: async (response) => {
        this.isLoading = false;
        this.disableAllFields();
        await this._alert.success('The team has been notified that the event is being scheduled.');
      },
      error: async (error) => {
        console.error('Error sending email:', error);
        this.isLoading = false;
        await this._alert.error('Error sending email to the requestor. Please try again later.', this._alert.getErrorDetails(error));
      }
    });
  }

  /**
   * Validates that required fields are filled out before allowing send to schedule
   * @returns Array of missing field names
   */
  private validateSendToScheduleFields(): string[] {
    const missing: string[] = [];

    // Check Assigned To field
    const assignedTo = this.eventForm.get('assignedTo')?.value;
    if (!assignedTo || assignedTo === '') {
      missing.push('Assigned To');
    }

    // Check Tools field - can be from dropdown selection OR custom input
    // Skip tools validation for Special Request and Holiday/Vacation/Internal Testing
    const requestType = this.eventForm.get('requestType')?.value;
    if (requestType !== 'Special Request' && requestType !== 'Holiday/Vacation/Internal Testing') {
      const toolsValue = this.eventForm.get('toolsToBeUsed')?.value;
      const isCustomToolsSelected = this.eventForm.get('SpecifyYourOwnValueTools')?.value;
      const customToolsValue = this.eventForm.get('toolsOther')?.value;

      const hasTools = (toolsValue && toolsValue !== '') || (isCustomToolsSelected && customToolsValue && customToolsValue !== '');
      if (!hasTools) {
        missing.push('Tools To Be Used');
      }
    }

    return missing;
  }

  /**
   * Handles the approval of the event.
   */
  async approveEvent() {
    const requestType = this.eventForm.get('requestType')?.value;
    const requireEventLeader = ['Live Event', 'Recording', 'Special Request'].includes(requestType);
    const canProceed = await this.ensurePersonMetaResolved({
      requireEventLeader,
      onlyIfEntered: false,
      actionLabel: 'approving this event'
    });
    if (!canProceed) return;

    // Ensure multi-selects and date/time fields are in a consistent state
    this.allMultifieldUpdate();

    if (!this.eventForm.value.assignedTo || this.eventForm.value.assignedTo == '' || !this.eventForm.value.requestor || this.eventForm.value.requestor == '') {
      await this._alert.error('Please ensure both Assigned To and Requestor fields are filled out before approving the event.');
      return;
    }

    this.isLoading = true;

    this.eventForm.get('workflow')?.setValue(this.eventForm.value.workflow + ' -> Approved');
    this.eventForm.get('requestStatus')?.setValue('Approved');
    this.cachedForm = { ...this.eventForm.value };

    // Persist the approved state to the backend first
    const payload = this._db.generateEventPayload(this.eventForm.value, this.eventForm.value.workflow);
    payload.user = { name: this.user.displayName };

    this._db.updateEventById(this.eventForm.value.event_id, payload).subscribe({
      next: () => {
        // Record version history for the approval changes
        const changes = this.recordVersionHistoryIfChanged();
        if (changes.length) {
          console.debug('[history] approve changes', changes);
          if (!this.serverAutoCapturesHistory && this.eventForm.value.event_id) {
            this._db.postEventHistory(this.eventForm.value.event_id, changes).subscribe();
          }
        }

        // After saving, send the approval email
        this._graph.sendApproveMail(payload).subscribe({
          next: async () => {
            this.isLoading = false;
            // Exit edit mode so the user sees the standard view with the Edit option
            this.disableAllFields();
            await this._alert.success('The requestor has been notified that the event has been approved and your changes have been saved.');
          },
          error: async (error) => {
            console.error('Error sending email:', error);
            this.isLoading = false;
            // Still exit edit mode because the event itself is saved
            this.disableAllFields();
            await this._alert.error('The event was saved as Approved, but there was an error sending the email to the requestor. Please verify and contact them directly if needed.', this._alert.getErrorDetails(error));
          }
        });
      },
      error: async (error) => {
        console.error('Error updating event during approval:', error);
        this.isLoading = false;
        await this._alert.error('Error saving the event as Approved. Please try again later.', this._alert.getErrorDetails(error));
      }
    });
  }

  async sendEventLink() {
    const requestType = this.eventForm.get('requestType')?.value;
    const requireEventLeader = ['Live Event', 'Recording', 'Special Request'].includes(requestType);
    const canProceed = await this.ensurePersonMetaResolved({
      requireEventLeader,
      onlyIfEntered: false,
      actionLabel: 'sending event links'
    });
    if (!canProceed) return;

    if (!this.eventForm.value.assignedTo || this.eventForm.value.assignedTo == '' || !this.eventForm.value.requestor || this.eventForm.value.requestor == '') {
      await this._alert.error('Please ensure both Assigned To and Requestor fields are filled out before sharing the event links.');
      return;
    }
    if ((!this.eventForm.value.gmVideoUrl || this.eventForm.value.gmVideoUrl == '') &&
      (!this.eventForm.value.virtualEventsUrl || this.eventForm.value.virtualEventsUrl == '') &&
      (!this.eventForm.value.brightcoveUrl || this.eventForm.value.brightcoveUrl == '') &&
      (!this.eventForm.value.teamsUrl || this.eventForm.value.teamsUrl == '')) {
      await this._alert.error('Please ensure at least one event link is provided before sharing the event links.');
      return;
    }
    this.isLoading = true;

    this.eventForm.get('workflow')?.setValue(this.eventForm.value.workflow + ' -> LinkSent');
    this.cachedForm = { ...this.eventForm.value };

    let payload = this._db.generateEventPayload(this.eventForm.value, this.eventForm.value.workflow);
    payload.user = { name: this.user.displayName };

    this._graph.sendLinkMail(payload).subscribe({
      next: async (response) => {
        this.isLoading = false;
        this.disableAllFields();
        await this._alert.success('The requestor has been notified that the event link has been sent.');
      },
      error: async (error) => {
        console.error('Error sending email:', error);
        this.isLoading = false;
        await this._alert.error('Error sending email to the requestor. Please try again later.', this._alert.getErrorDetails(error));
      }
    });
  }

  async completeEvent() {
    const requestType = this.eventForm.get('requestType')?.value;
    const requireEventLeader = ['Live Event', 'Recording', 'Special Request'].includes(requestType);
    const canProceed = await this.ensurePersonMetaResolved({
      requireEventLeader,
      onlyIfEntered: false,
      actionLabel: 'completing this event'
    });
    if (!canProceed) return;

    if (!this.eventForm.value.assignedTo || this.eventForm.value.assignedTo == '' || !this.eventForm.value.requestor || this.eventForm.value.requestor == '') {
      await this._alert.error('Please ensure both Assigned To and Requestor fields are filled out before completing the event.');
      return;
    }
    this.isLoading = true;

    this.eventForm.get('workflow')?.setValue(this.eventForm.value.workflow + ' -> Completed');
    this.eventForm.get('requestStatus')?.setValue('Completed');
    this.eventForm.get('eventOwnershipTransferred')?.setValue('Yes');
    this.cachedForm = { ...this.eventForm.value };
    let payload = this._db.generateEventPayload(this.eventForm.value, this.eventForm.value.workflow);
    payload.user = { name: this.user.displayName };
    this._graph.sendCompleteMail(payload).subscribe({
      next: async (response) => {
        this.isLoading = false;
        this.disableAllFields();
        await this._alert.success('The requestor has been notified that the event is completed');
      },
      error: async (error) => {
        console.error('Error sending email:', error);
        this.isLoading = false;
        await this._alert.error('Error sending email to the requestor. Please try again later.', this._alert.getErrorDetails(error));
      }
    });

  }

  async onDelete() {
    const confirmed = await this._alert.confirm('Are you sure you want to delete this event? This action cannot be undone.');
    if (confirmed && this.eventForm.value.event_id > 0) {
      // this.isLoading = true;
      this._db.deleteEventById(this.eventForm.value.event_id).subscribe({
        next: async (response) => {
          // this.isLoading = false;
          // await this._alert.success('Event deleted successfully.');
        },
        error: async (error) => {
          console.error('Error deleting event:', error);
          this.isLoading = false;
          await this._alert.error('Error deleting event. Please try again later.', this._alert.getErrorDetails(error));
        }
      });
      this.router.navigate(['/calendar']);
    } else if (confirmed) {
      this.router.navigate(['/calendar']);
    }

  }

  async cancelEvent() {
    const requestType = this.eventForm.get('requestType')?.value;
    const requireEventLeader = ['Live Event', 'Recording', 'Special Request'].includes(requestType);
    const canProceed = await this.ensurePersonMetaResolved({
      requireEventLeader,
      onlyIfEntered: false,
      actionLabel: 'canceling this event'
    });
    if (!canProceed) return;

    if (!this.eventForm.value.requestor || this.eventForm.value.requestor == '') {
      await this._alert.error('Please ensure the Requestor field is filled out before canceling the event.');
      return;
    }
    this.isLoading = true;

    this.eventForm.get('workflow')?.setValue(this.eventForm.value.workflow + ' -> Canceled');
    this.eventForm.get('requestStatus')?.setValue('Canceled');
    this.cachedForm = { ...this.eventForm.value };

    let payload = this._db.generateEventPayload(this.eventForm.value, this.eventForm.value.workflow);
    payload.user = { name: this.user.displayName };

    this._graph.sendCancelMail(payload).subscribe({
      next: async (response) => {
        this.isLoading = false;
        this.disableAllFields();
        await this._alert.success('The requestor has been notified that the event has been canceled.');
      },
      error: async (error) => {
        console.error('Error sending email:', error);
        this.isLoading = false;
        await this._alert.error('Error sending email to the requestor. Please try again later.', this._alert.getErrorDetails(error));
      }
    });
  }

  /**
   * Cancels any changes made since entering edit mode and disables editing
   * Reverts the form to the cached state without saving
   */
  async cancelChanges() {
    if (!this.cachedForm) {
      console.warn('No cached form data available to revert to');
      this.disableAllFields();
      return;
    }

    // Show confirmation dialog to prevent accidental cancellation
    const confirmed = await this._alert.confirm('Are you sure you want to cancel all changes? Any unsaved changes will be lost.');
    if (confirmed) {
      console.log('Cancelling changes and reverting to cached form');

      // Revert the form to the cached state
      this.eventForm.patchValue(this.cachedForm);

      // Handle special fields that might need individual attention
      this.restoreSpecialFields();
      
      // Refresh date/time fields after restoring cached form
      this.refreshDateTimeFields();

      // Disable edit mode
      this.disableAllFields();

      console.log('Form reverted to cached state and edit mode disabled');
    }
  }

  /**
   * Restores special fields that might need individual handling during revert
   */
  private restoreSpecialFields(): void {
    // Handle date fields properly
    if (this.cachedForm.eventStartDate) {
      this.eventForm.get('eventStartDate')?.setValue(new Date(this.cachedForm.eventStartDate));
    }
    if (this.cachedForm.eventEndDate) {
      this.eventForm.get('eventEndDate')?.setValue(new Date(this.cachedForm.eventEndDate));
    }
    if (this.cachedForm.vodExpirationDate) {
      this.eventForm.get('vodExpirationDate')?.setValue(new Date(this.cachedForm.vodExpirationDate));
    }

    // Handle time dropdown fields using 24-hour format
    if (this.cachedForm.eventStartDate) {
      const startDate = new Date(this.cachedForm.eventStartDate);
      this.eventForm.patchValue({
        startHour: startDate.getHours(), // Use 24-hour format (0-23)
        startMinute: startDate.getMinutes().toString().padStart(2, '0')
      });
    }

    if (this.cachedForm.eventEndDate) {
      const endDate = new Date(this.cachedForm.eventEndDate);
      this.eventForm.patchValue({
        endHour: endDate.getHours(), // Use 24-hour format (0-23)
        endMinute: endDate.getMinutes().toString().padStart(2, '0')
      });
    }

    // Restore requestor and event leader links if they exist
    // Only search if metadata is incomplete - this prevents dialog popup on load
    if (this.cachedForm.requestor) {
      const requestorMeta = this.cachedForm.requestorMeta;
      if (requestorMeta && requestorMeta.gmid && requestorMeta.name) {
        // Use existing complete metadata instead of searching
        this.requestorName = requestorMeta.name;
        this.requestorLink = `https://peoplefinder.gm.com/person/show/${requestorMeta.gmid}`;
        this.isRequestorLinkVisible = true;
      } else {
        // Metadata incomplete - search to populate it
        this.searchRequestor();
      }
    }
    if (this.cachedForm.eventLeader) {
      const eventLeaderMeta = this.cachedForm.eventLeaderMeta;
      if (eventLeaderMeta && eventLeaderMeta.gmid && eventLeaderMeta.name) {
        // Use existing complete metadata instead of searching
        this.eventLeaderName = eventLeaderMeta.name;
        this.eventLeaderLink = `https://peoplefinder.gm.com/person/show/${eventLeaderMeta.gmid}`;
        this.isEventLeaderLinkVisible = true;
      } else {
        // Metadata incomplete - search to populate it
        this.searchEventLeader();
      }
    }

    // Handle array fields for multi-select
    if (Array.isArray(this.cachedForm.tech)) {
      this.eventForm.get('tech')?.setValue(this.cachedForm.tech);
    }
  }

  private formatDateTime(dt: Date | null): string {
    if (!dt) return '';
    const d = new Date(dt);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    let hour = d.getHours();
    const minute = d.getMinutes().toString().padStart(2, '0');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${month}/${day}/${year} ${hour}:${minute} ${ampm}`;
  }

  private resolveDisplayList(raw: string, dict: { [k: string]: string } | null): string {
    if (!raw) return '';
    if (!dict) return raw;
    return raw.split(',').map(v => v.trim()).filter(v => v)
      .map(k => dict[k] || k).join(',');
  }

  copySummary() {
    const title = this.eventForm.get('requestTitle')?.value || '';
    const eventLeader = this.eventLeaderName || this.eventForm.get('eventLeader')?.value || '';
    const startDate: Date | null = this.eventForm.get('eventStartDate')?.value || null;
    const endDate: Date | null = this.eventForm.get('eventEndDate')?.value || null;
    const expected = this.eventForm.get('expectedNumberOfParticipants')?.value || '';
    const primaryLocation = this.eventForm.get('primaryPresentingLocation')?.value || '';
    const coordinator = this.requestorName || this.eventForm.get('requestor')?.value || '';
    const broadcastLead = this.eventForm.get('assignedTo')?.value || '';
    const audienceRaw = this.eventForm.get('audience')?.value || '';
    const toolsRaw = this.eventForm.get('toolsToBeUsed')?.value || '';
    const gmVideoUrl = this.eventForm.get('gmVideoUrl')?.value || 'n/a';
    const virtualEventsUrl = this.eventForm.get('virtualEventsUrl')?.value || '';
    const teamsUrl = this.eventForm.get('teamsUrl')?.value || '';
    const brightcoveUrl = this.eventForm.get('brightcoveUrl')?.value || '';
    const broadcastComments = this.eventForm.get('broadcastComments')?.value || '';

    const audienceDisplay = this.resolveDisplayList(audienceRaw, this.audienceOptions);
    const toolsDisplay = this.resolveDisplayList(toolsRaw, this.toolsOptions);

    const summary =
      `Event: ${title}
      Event Leader:  ${eventLeader}
      Start Date and Time:  ${this.formatDateTime(startDate)}
      End Date and Time:  ${this.formatDateTime(endDate)}
      Expected Number of Participants:  ${expected}
      Primary Location:  ${primaryLocation || ''}
      Event Requestor:  ${coordinator}
      Broadcast Lead:  ${broadcastLead}
      Audience:  ${audienceDisplay}
      Tools Used:  ${toolsDisplay}
      GM Video URL:  ${gmVideoUrl}
      Virtual Event URL:  ${virtualEventsUrl}
      Teams URL:  ${teamsUrl}
      Brightcove URL:  ${brightcoveUrl}
      Broadcast Comments:  ${broadcastComments}`;

    const doAlert = async () => {
      await this._alert.success('Summary is copied to clipboard. Just paste it to the destination by ctrl+v');
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(summary)
        .then(() => doAlert())
        // .catch(async () => {
        //   this.fallbackCopy(summary);
        //   await doAlert();
        // });
    } else {
      // this.fallbackCopy(summary);
      // doAlert();
    }
  }
}